/*
 * @providesModule MatchSelectCell
 *
 */

import React, {useRef, useEffect, useState} from 'react';

import _ from 'lodash';
import moment from 'moment';
import * as Animatable from 'react-native-animatable';
import AnimatedDiscountBadge from './AnimatedDiscountBadge';
import AvatarView from 'AvatarView';
import AWSManager from 'AWSManager';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrencyString from 'CurrencyString';
import CurrentUserStore from 'CurrentUserStore';
import Device from 'Device';
import FriendsNewBanner from 'FriendsNewBanner';
import FTUEController from 'FTUEController';
import I18n, {_m} from 'INTL';
import Label from 'Label';
import LinearGradient from 'react-native-linear-gradient';
import RegistrationBanner from 'RegistrationBanner';
import NativeContactsManager from 'NativeContactsManager';
import Optional from 'Optional';
import PlayableMatchesActionCreator from 'PlayableMatchesActionCreator';
import {PlayGradientButton} from 'GradientButtons';
import Preferences from 'Preferences';
import PrizeBackground from 'PrizeBackground';
import RenderUtils from 'RenderUtils';
import SheenEffect from 'SheenEffect';
import Sizes from 'Sizes';
import SkillzImage from 'SkillzImage';
import SkillzModule from 'SkillzModule';
import SkillzRouter from 'SkillzRouter';
import SkillzStyle, {darkenColor, lightenColor} from 'SkillzStyle';
import SkillzSVG from 'SkillzSVG';
import SKZConstants from 'SKZConstants';
import StringUtils from 'StringUtils';
import styles, {
  PRIZE_BACKGROUND_WIDTH,
  PRIZE_BACKGROUND_HEIGHT,
  PRIZE_POOL_FONT,
  dynamicFontSize,
} from 'MatchSelectCellStyle';
import Tournament from 'SKZTournament';
import {getMedalIcon} from 'LeagueInfoUtils';
import {LeagueMedal, ELeagueMedal} from '@skillz/ui';
import {Svg, Text as SVGText} from 'react-native-svg';
import TournamentHighlightBorder from 'TournamentHighlightBorder';
import {
  Animated,
  findNodeHandle,
  NativeModules,
  Platform,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  UIManager,
  Text,
  View,
  ViewStyle,
  Easing,
} from 'react-native';
import GlobalEventEmitter from 'GlobalEventEmitter';
import {FRIENDS_TOURNAMENT_TYPE} from 'FriendsTournamentController';
import {
  PLAY_MATCH_SELECT,
  LIVE_BRACKET_TEMPLATE_AVAILABLE,
  LIVE_BRACKET_ICON_HOME_CLICK,
} from 'AWSConstants';
import LiveBracketTrackingHelper from 'LiveBracketTrackingHelper';
import {LiveBracketsButton, LiveBracketsReadyToPlayButton} from 'LiveBracketsButton';
import AnimatedDiscountFee from './AnimatedDiscountFee';
import LiveBracketTimerUtils from 'LiveBracketTimerUtils';
import {EmitterSubscription} from 'react-native';
import Visibility from 'Visibility';
import {trackTelemetryEvent} from 'TrackTelemetryEvent';
import {TelemetryEventName} from 'TelemetryConstants';
import {createAccessibilityLabel} from 'AccessibilityHelpers';

const {
  featuredMatchBarColor,
  fontSizeH3,
  playButtonGradient,
  textColorCash,
  textColorTicketz,
  textColorZ,
  fontSizeH6,
  fontSecondary,
  fontBold,
  practiceCurrencyFullIconLargeURI,
} = SkillzStyle.fullStyles;

const RCTUIManager = NativeModules.UIManager;

const MILLISECONDS_PER_SECOND = SKZConstants.MS_PER_SEC;
const ONE_BILLION = 1000000000;

const PULSE_ANIMATION = {
  0: {
    scale: 1,
  },
  0.5: {
    scale: 0.975,
  },
  1: {
    scale: 1,
  },
} as Animatable.CustomAnimation;

const DISCOUNT_PULSE_ANIMATION = {
  0: {
    scale: 1,
  },
  0.5: {
    scale: 0.93,
  },
  1: {
    scale: 1,
  },
} as Animatable.CustomAnimation;

const TEST_IDS = {
  MATCH_SELECT_CELL_PRIZE: 'match_select_cell_prize',
  MATCH_SELECT_CELL_DESCRIPTION: 'match_select_cell_description',
  MATCH_SELECT_CELL_DETAILS: 'match_select_cell_details',
  MATCH_SELECT_CELL_PLAY_BUTTON: 'match_select_cell_play_button',
  MATCH_SELECT_CELL_PRIZES: 'match_select_cell_prizes',
  MATCH_SELECT_CELL_PLAYERS: 'match_select_cell_players',
  MATCH_SELECT_CELL_ENTRY_FEE: 'match_select_cell_entry_fee',
};

export type MatchSelectCellProps = {
  avatarUrl: string;
  cellIndex: number;
  displayOnly: boolean;
  fromVSScreen: boolean;
  hasExternalBanner: boolean;
  hasMinimumPlayers: boolean;
  isFriendsTournament: boolean;
  isSync: boolean;
  isTournamentCreator: boolean;
  mainText: string;
  matchSelectList: any;
  onFriendsTournamentExpired: Function;
  onFriendsTournamentShouldReset: Function;
  onHighlight: Function;
  onPress: Function;
  overtime: boolean;
  parent: any;
  rowData: Tournament;
  scaleToContainer: boolean;
  sectionHeader: string;
  sectionType: string;
  setPlayButtonCallback: Function;
  setPlayButtonRef: Function;
  updatePlayButton: Function;
  useSmallCell: boolean;
  isSyncFTUE: boolean;
  testIDOverride: string;
  shouldShowDiscountAnimation?: boolean;
  isInViewport?: boolean;
  isHighlighted?: boolean; // Whether this tournament should show highlight border
  isHighlightForced?: boolean; // Whether this is a forced highlight (overlay) or non-forced (in-cell border)
  hasAlreadyAnimated?: boolean;
  onAnimationComplete?: (tournamentId: string) => void;
  hasCustomBorder?: boolean;
  customBorderProps?: {
    borderWidth: number;
    borderColor: string;
  };
};

export type MatchSelectCellStates = {
  scalingAnimation: unknown;
  ftResetIn: number | null;
  limitedUseEntriesRemaining: number | null | undefined;
  timeUntil?: string | null;
  cellDimensions?: {width: number; height: number}; // Store actual cell dimensions for border
  currentUser: {
    avatar_url?: string;
    username?: string;
  };
  ticketzString: string;
  playButtonHighlighted: boolean;
  prizeString: string;
  entryFeeString: string;
  sectionList: unknown;
  tournamentExpired: boolean;
  endsIn?: string;
  matchAbortedIn?: string;
  scrolled?: boolean;
  hasEnteredViewport: boolean;
  hasDiscountAnimated: boolean; // Track if discount animation has already played
};

interface LiveBracketTrackingData {
  total_number_player_needed: number;
  bracket_id: string;
  players_filled: number;
  bracket_entry_fee: number;
  game_id: string;
  wallet_balance: number;
}

export default class MatchSelectCell extends React.Component<
  MatchSelectCellProps,
  MatchSelectCellStates
> {
  static defaultProps = {
    fromVSScreen: false,
    shouldUpdateEndsIn: true,
    isFriendsTournament: false,
  };

  updateEventTimesTimer?: number;
  tournament: Tournament;
  bounceValue: Animated.Value;
  scrolled: boolean;
  onPress: Function;
  measurePlayButtonTimeout?: NodeJS.Timeout;
  scrollToHighlightTimeout?: NodeJS.Timeout;
  friendsResetTimeout?: NodeJS.Timeout;
  discountAnimationTimeout?: NodeJS.Timeout;
  scrollToCellTimeout?: NodeJS.Timeout;
  layoutTimeout?: NodeJS.Timeout;
  adjustingStyles: Partial<typeof styles>;
  playButtonRef: React.RefObject<unknown>;
  seenTournaments: unknown;
  sectionList: unknown;
  friendsTournamentTimeInterval: unknown;
  updateLimitedUseTimer: unknown;
  subscriptions: unknown[];
  tournamentExpired: unknown;
  cell: React.ComponentClass<any, any> | null = null;
  isTournamentTemplateUIV2 = ClientPropertyConstants.getTournamentTemplateUIV2Enabled();

  constructor(params: MatchSelectCellProps) {
    super(params);
    this.tournament = new Tournament(params.rowData);
    this.bounceValue = new Animated.Value(1);
    this.scrolled = false;
    this.onPress = params.onPress;
    this.subscriptions = [];
    this.liveBracketTrackingData = null;

    this.state = {
      currentUser: {
        avatar_url: 'skillz-generic-avatar',
      },
      entryFeeString: this.tournament.formatted_entry_fee,
      playButtonHighlighted: false,
      prizeString: this.tournament?.getPrizeString(),
      limitedUseEntriesRemaining: this.tournament?.getLimitedUseTournamentEntries(),
      scalingAnimation: null,
      sectionList: params.parent,
      ticketzString: this._getTicketzString(params.rowData),
      tournamentExpired: false,
      ftResetIn: this._getSecondsToNextFriendChallenge() || null,
      hasEnteredViewport: false,
      hasDiscountAnimated: false, // Initialize discount animation state
    };
    this._scrollToAndAnimateIfHighlighted(params.rowData);
    this.adjustingStyles = this._getStylesToUse();
    this.playButtonRef = React.createRef();
  }
  _shouldShowDiscountBadge(): boolean {
    return !!(
      this.tournament?.cash_fee_discount &&
      typeof this.tournament?.cash_fee_discount?.discount_percent === 'number' &&
      this.tournament?.cash_fee_discount?.discount_percent > 0 &&
      this.tournament?.cash_fee_discount?.discounted_cash_fee >= 0
    );
  }

  _shouldShowZBCDiscount(): boolean {
    return this.tournament?.isAnyZEntryBonusCashPrizeTournament() || false;
  }

  _shouldShowAnyDiscount(): boolean {
    return this._shouldShowDiscountBadge() || this._shouldShowZBCDiscount();
  }

  _shouldShowPercentageBadge(): boolean {
    // Only show percentage badge for regular discounts, not Z+BC
    return this._shouldShowDiscountBadge() && !this._shouldShowZBCDiscount();
  }

  _getZBCSlashedEntryFee(): number {
    if (!this._shouldShowZBCDiscount()) {
      return 1; // Default fallback
    }

    const zFee = this.tournament?.z_fee || 0;

    // TODO: This is for Z to BC template experiment. Remove this once we have a proper Z to BC discount logic
    if (zFee <= 150) {
      return 0.3;
    } else if (zFee >= 151 && zFee <= 400) {
      return 0.5;
    } else if (zFee > 400) {
      return 1;
    }

    return 1; // Default fallback
  }

  componentDidMount() {
    this._fetchUser();
    this._addListeners();

    const seenTournamentsString = Preferences.get(
      SKZConstants.FRIENDS_TOURNAMENTS_SEEN_TOURNAMENTS
    );

    if (seenTournamentsString) {
      this.seenTournaments = JSON.parse(seenTournamentsString);
    }
    this.onFocus();

    // Track Live Bracket Template Available when player sees live bracket option on home screen
    if (this.tournament.isLiveBracket()) {
      trackTelemetryEvent({
        event: TelemetryEventName.LIVE_BRACKET_TEMPLATE_AVAILABLE,
        payload: LiveBracketTrackingHelper.getLiveBracketTrackingDataV2(this.tournament),
      });
    }

    this.measurePlayButtonTimeout = setTimeout(() => {
      this._measurePlayButtonForArrow(this.playButtonRef);
    }, 500);
  }

  componentDidUpdate(prevProps: MatchSelectCellProps) {
    if (this.props.parent !== prevProps.parent) {
      this.sectionList = this.props.parent;
    }
    if (this.props.rowData !== prevProps.rowData) {
      this.state.scalingAnimation?.stop();
      this.tournament = new Tournament(this.props.rowData);
      this.bounceValue = new Animated.Value(1);
      this.scrolled = false;
      this.onPress = this.props.onPress;

      this.setState(
        {
          entryFeeString: this.tournament.formatted_entry_fee,
          prizeString: this.tournament?.getPrizeString(),
          limitedUseEntriesRemaining: this.tournament?.getLimitedUseTournamentEntries(),
          ticketzString: this._getTicketzString(this.props.rowData),
          scalingAnimation: null,
          ftResetIn: this._getSecondsToNextFriendChallenge() || null,
        },
        () => {
          if (this._getSecondsToNextFriendChallenge() && !this.friendsTournamentTimeInterval) {
            this.startFriendsTimer();
          }
        }
      );

      this.scrollToHighlightTimeout = setTimeout(() => {
        this._scrollToAndAnimateIfHighlighted();
      }, MILLISECONDS_PER_SECOND);
    }
  }

  componentWillUnmount() {
    this._stopScalingAnimation();
    this.stopEventTimer();
    this.stopFriendsTimer();
    this.stopLimitedUseTimer();
    this._removeListeners();

    // Clean up all timers
    if (this.measurePlayButtonTimeout) {
      clearTimeout(this.measurePlayButtonTimeout);
      this.measurePlayButtonTimeout = undefined;
    }
    if (this.scrollToHighlightTimeout) {
      clearTimeout(this.scrollToHighlightTimeout);
      this.scrollToHighlightTimeout = undefined;
    }
    if (this.friendsResetTimeout) {
      clearTimeout(this.friendsResetTimeout);
      this.friendsResetTimeout = undefined;
    }
    if (this.discountAnimationTimeout) {
      clearTimeout(this.discountAnimationTimeout);
      this.discountAnimationTimeout = undefined;
    }
    if (this.scrollToCellTimeout) {
      clearTimeout(this.scrollToCellTimeout);
      this.scrollToCellTimeout = undefined;
    }
    if (this.layoutTimeout) {
      clearTimeout(this.layoutTimeout);
      this.layoutTimeout = undefined;
    }
  }

  shouldComponentUpdate(nextProps: Object, nextState: Object): boolean {
    return RenderUtils.shouldAllowStateChangeRerender(
      this.state,
      nextState,
      [],
      this.props,
      nextProps
    );
  }

  onFocus() {
    this.startScalingAnimation();
    this.startEventTimer();
    this.startFriendsTimer();
    this.startLimitedUseTimer();
  }

  onBlur() {
    this.stopScalingAnimation();
    this.stopEventTimer();
    this.stopFriendsTimer();
    this.stopLimitedUseTimer();
  }

  hasScalingAnimation(): boolean {
    return this._isHighlightCell() && !this.scrolled && !this.props.isSyncFTUE;
  }

  hasEventTimer(): boolean {
    return Boolean(this.tournament.live_event || this.tournament.isLiveBracket());
  }

  hasLimitedUseTimer(): boolean {
    return (
      this.tournament?.isLimitedUseTournament() &&
      this.tournament?.getLimitedUseTournamentType() === 'TIME'
    );
  }

  hasFriendsTimer(): boolean {
    return Boolean(
      (this.tournament.friend && this.tournament.creator) || this._getSecondsToNextFriendChallenge()
    );
  }

  startEventTimer() {
    this.stopEventTimer();
    if (this.hasEventTimer()) {
      this._updateEventTimes();
      this.updateEventTimesTimer = setInterval(() => {
        this._updateEventTimes();
      }, MILLISECONDS_PER_SECOND);
    }
  }

  stopEventTimer() {
    if (this.updateEventTimesTimer) {
      clearInterval(this.updateEventTimesTimer);
      this.updateEventTimesTimer = undefined;
    }
  }

  startLimitedUseTimer() {
    this.stopLimitedUseTimer();
    if (this.hasLimitedUseTimer()) {
      this._updateLimitedUseTimer();
      this.updateLimitedUseTimer = setInterval(() => {
        this._updateLimitedUseTimer();
      }, MILLISECONDS_PER_SECOND);
    }
  }

  stopLimitedUseTimer() {
    if (this.updateLimitedUseTimer) {
      clearInterval(this.updateLimitedUseTimer);
      this.updateLimitedUseTimer = undefined;
    }
  }

  startFriendsTimer() {
    this.stopFriendsTimer();
    if (this.hasFriendsTimer()) {
      if (this.state.ftResetIn != null) {
        this._updateFriendsTournamentResetTime();
        this.friendsTournamentTimeInterval = setInterval(() => {
          this._updateFriendsTournamentResetTime();
        }, MILLISECONDS_PER_SECOND);
      } else {
        this._updateFriendsTournamentTime();
        this.friendsTournamentTimeInterval = setInterval(() => {
          this._updateFriendsTournamentTime();
        }, MILLISECONDS_PER_SECOND);
      }
    }
  }

  stopFriendsTimer() {
    if (this.friendsTournamentTimeInterval) {
      clearInterval(this.friendsTournamentTimeInterval);
      this.friendsTournamentTimeInterval = 0;
    }
  }

  _fetchUser() {
    CurrentUserStore.fetchUser(true).then((user: Object) => {
      this.setState({
        currentUser: user,
      });
    });
  }

  _initializeState(): Object {
    this.tournament = new Tournament(this.props.rowData);
    return {
      currentUser: {
        avatar_url: 'skillz-generic-avatar',
      },
      entryFeeString: this.tournament.formatted_entry_fee,
      onPress: this.props.onPress,
      playButtonHighlighted: false,
      prizeString: this.tournament?.getPrizeString(),
      limitedUseEntriesRemaining: this.tournament?.getLimitedUseTournamentEntries(),
      sectionList: this.props.parent,
      ticketzString: this._getTicketzString(this.props.rowData),
      tournamentExpired: false,
      ftResetIn: this._getSecondsToNextFriendChallenge() || null,
      hasEnteredViewport: false,
    };
  }

  _addListeners() {
    this._removeListeners();
    this.subscriptions = [];
    this.subscriptions.push(
      GlobalEventEmitter.addListener('MatchSelectViewWillAppear', () => {
        this._scrollToAndAnimateIfHighlighted();
        this.onFocus();
      }),
      GlobalEventEmitter.addListener('MatchSelectViewWillDisappear', () => {
        this.setState({scrolled: false});
        this.onBlur();
      })
    );
    if (
      this.hasScalingAnimation() ||
      this.hasEventTimer() ||
      this.hasFriendsTimer() ||
      this.hasLimitedUseTimer()
    ) {
      this.subscriptions.push(
        GlobalEventEmitter.addListener('VersusWillAppear', () => {
          this.onBlur();
        }),

        GlobalEventEmitter.addListener('RegularPLOWWillAppear', () => {
          this.onBlur();
        }),

        GlobalEventEmitter.addListener('RegularPLOWDidAppear', () => {
          this.onBlur();
        }),

        GlobalEventEmitter.addListener('HomeScreenDidAppear', () => {
          this.onFocus();
        })
      );
    }
  }

  _removeListeners() {
    if (this.subscriptions) {
      this.subscriptions.forEach((listener: EmitterSubscription) => {
        listener?.remove();
      });
      this.subscriptions = [];
    }
  }

  _getSecondsToNextFriendChallenge(): number {
    return this.tournament.getSecondsToNextChallengeNumber();
  }

  _updateSecondsToNextFriendChallenge(newSeconds: number) {
    this.tournament.seconds_to_next_challenge = newSeconds;
  }

  _updateFriendsTournamentResetTime() {
    if (this.state.ftResetIn && this.state.ftResetIn > 0) {
      const newResetTime = this.state.ftResetIn - 1;
      this._updateSecondsToNextFriendChallenge(newResetTime);
      this.setState({ftResetIn: newResetTime});
    } else {
      this.friendsResetTimeout = setTimeout(() => {
        if (this.props.onFriendsTournamentShouldReset) {
          this.props.onFriendsTournamentShouldReset();
        } else {
          PlayableMatchesActionCreator.fetchMatchList(false);
        }
      }, 200); // Adding a slight delay so we don't refresh when exactly seconds_to_next_challenge is 0
      clearInterval(this.friendsTournamentTimeInterval);
      this.friendsTournamentTimeInterval = null;
    }
  }

  _updateFriendsTournamentTime() {
    const timeUntil = I18n.daysHrsMinsSecsUntil(this.tournament.end);
    const now = new Date();
    const expiration = new Date(this.tournament.end);

    if (expiration < now && this.friendsTournamentTimeInterval) {
      if (this.props.onFriendsTournamentExpired) {
        this.props.onFriendsTournamentExpired();
      } else {
        PlayableMatchesActionCreator.fetchMatchList(false);
      }
      clearInterval(this.friendsTournamentTimeInterval);
      this.friendsTournamentTimeInterval = null;

      this.setState({
        endsIn: _m('Expired'),
        tournamentExpired: true,
      });
    } else {
      this.setState({
        endsIn: timeUntil,
      });
    }
  }

  _isNoEntryFee(): boolean {
    const tournament = this.tournament;
    return (
      tournament.hasVideoAdEntriesRemaining() ||
      !(tournament.cash_fee > 0.0 || tournament.z_fee > 0.0)
    );
  }

  // Check if discount animation should play (first time in viewport only)
  _shouldPlayDiscountAnimation(): boolean {
    const hasDiscountData = this._shouldShowAnyDiscount();

    const shouldAnimate =
      this.props.shouldShowDiscountAnimation === true &&
      hasDiscountData &&
      this.props.isInViewport === true;

    // If animation should play, mark tournament as animated and call callback
    if (shouldAnimate && this.props.onAnimationComplete) {
      // Schedule the callback to run after animation completes (4s total: 1s delay + 2s duration + 1s buffer)
      this.discountAnimationTimeout = setTimeout(() => {
        const tournamentId = this.tournament.id?.toString();
        if (tournamentId) {
          this.props.onAnimationComplete?.(tournamentId);
        }
      }, 8000);
    }

    return shouldAnimate;
  }

  _getTournamentEntryFeeString(): string {
    const tournament = this.tournament;
    if (this.props.overtime) {
      return _m('ENTRY PAID');
    }

    if (tournament.entries_required) {
      return (
        _m('ENTRIES') + ' ' + (tournament.live_event?.entries ? tournament.live_event.entries : '0')
      );
    }

    if (tournament?.category_type === 'LIVE' && tournament.hasFreeEntriesRemaining?.()) {
      return _m('FREE');
    } else if (tournament?.category_type === 'LIVE' && tournament.hasVideoAdEntriesRemaining()) {
      return _m('WATCH AD');
    } else if (this._isNoEntryFee()) {
      return _m('FREE');
    }

    if (this.isTournamentTemplateUIV2) {
      return tournament.formatted_entry_fee_V2;
    }

    if (tournament.isPointsTournament() || tournament.hasZEntryFee()) {
      return _m('ENTRY');
    }

    return _m('ENTRY') + ' ' + tournament.formatted_entry_fee;
  }

  _getFontSize() {
    if (Sizes.isAndroidSmallPortraitTablet() && this.props.scaleToContainer) {
      return styles.smallAndroidPortraitTabletFontSize;
    }

    return styles.dynamicFontSize;
  }

  _getPointsEntryFeeView(): React.ReactNode {
    const tournament = this.tournament;
    const textStyle = [styles.zEntryFeeText, this._getTextAdjustment(), this._getFontSize()];
    return (
      <View style={styles.matchDetailsView}>
        <Label minimumFontScale={0.25} style={textStyle}>
          {this._getTournamentEntryFeeString() +
            SkillzStyle.currencyValues.practiceCurrencyString +
            String(tournament.z_fee || 0)}
        </Label>
      </View>
    );
  }

  _getEntryFeeView(): React.ReactNode {
    const tournament = this.tournament;

    const entryFeeTextStyle = this._shouldRenderInstaMatch()
      ? styles.entryFeeTextInstaMatch
      : styles.entryFeeText;

    const textStyle = [entryFeeTextStyle, this._getTextAdjustment(), this._getFontSize()];

    if ((tournament.isPointsTournament() && !this._isNoEntryFee()) || tournament.hasZEntryFee()) {
      return this._getPointsEntryFeeView();
    }

    // If Instant Match, show typical entry_fee crossed out, with discounted entry_fee also shown
    if (this._shouldRenderInstaMatchDiscounted(tournament)) {
      return (
        <View
          style={styles.matchDetailsViewInstaMatch}
          testID={TEST_IDS.MATCH_SELECT_CELL_ENTRY_FEE}
        >
          <Label minimumFontScale={0.25} style={[textStyle, styles.entryTextInstaMatch]}>
            {_m('ENTRY')}
          </Label>
          <LinearGradient
            colors={['#EDEDED', '#DADADA']}
            style={styles.originalPriceGradientInstaMatch}
          >
            <Label minimumFontScale={0.25} style={textStyle}>
              {tournament.formatted_entry_fee}
            </Label>
            <SkillzImage
              style={styles.diagonalCrossOutIconInstaMatch}
              source={'ic_diagonal_top_left_to_bottom_right.svg'}
              tintColor={'#FF0000'}
              resizeMode={'stretch'}
              resizeMethod={'resize'}
            />
          </LinearGradient>
          <LinearGradient
            colors={['#74D083', '#157224']}
            style={styles.discountedPriceGradientInstaMatch}
          >
            <Label minimumFontScale={0.25} style={[textStyle, {color: 'white'}]}>
              {I18n.getCurrencyString(tournament.discounted_cash_fee!, '$', true, true)}
            </Label>
          </LinearGradient>
        </View>
      );
    } else if (this._shouldRenderInstaMatch()) {
      return (
        <View
          style={[styles.matchDetailsView, {justifyContent: 'flex-end'}]}
          testID={TEST_IDS.MATCH_SELECT_CELL_ENTRY_FEE}
        >
          <Label minimumFontScale={0.25} style={textStyle}>
            {this._getTournamentEntryFeeString()}
          </Label>
        </View>
      );
    }

    return (
      <View style={styles.matchDetailsView} testID={TEST_IDS.MATCH_SELECT_CELL_ENTRY_FEE}>
        <Label minimumFontScale={0.25} style={textStyle}>
          {this._getTournamentEntryFeeString()}
        </Label>
      </View>
    );
  }

  _getEntryFeeViewV2(): React.ReactNode {
    const tournament = this.tournament;
    if (this._shouldRenderInstaMatchDiscounted(tournament)) {
      const discountedCashFee = I18n.getCurrencyString(
        tournament.discounted_cash_fee!,
        '$',
        true,
        true
      ).slice(1);
      return (
        <View style={styles.playSection}>
          <Label style={styles.entryFeeTextV2}>{_m('ENTRY FEE:')}</Label>
          <View style={styles.currencyItemContainer}>
            <SkillzImage
              source={this.getTournamentEntryFeeIcon()}
              style={styles.entryFeeCurrencyImage}
              resizeMode={'contain'}
            />
            <View>
              <Label minimumFontScale={0.25} style={styles.entryFeePrizeDiscountedTextV2}>
                {tournament.formatted_entry_fee_V2}
              </Label>
              <SkillzImage
                style={styles.diagonalCrossOutIconInstaMatch}
                source={'ic_diagonal_top_left_to_bottom_right.svg'}
                tintColor={'#FF0000'}
                resizeMode={'stretch'}
                resizeMethod={'resize'}
              />
            </View>

            <Label style={styles.entryFeePrizeTextV2}>{discountedCashFee}</Label>
          </View>
        </View>
      );
    }

    return (
      <View
        style={
          !this.props.displayOnly && !this._shouldRenderInstaMatch()
            ? styles.playSection
            : styles.playSectionCentered
        }
      >
        <Label style={styles.entryFeeTextV2}>{_m('ENTRY FEE:')}</Label>
        {this._shouldShowAnyDiscount() ? (
          <View style={styles.currencyItemContainer}>
            <AnimatedDiscountFee
              entryFee={
                this._shouldShowZBCDiscount()
                  ? this._getZBCSlashedEntryFee()
                  : this.tournament?.cash_fee
              }
              discountObject={this.tournament?.cash_fee_discount}
              shouldTriggerAnimation={this._shouldPlayDiscountAnimation()}
              hasAnimated={this.props.hasAlreadyAnimated}
              isZBCTournament={this._shouldShowZBCDiscount()}
              zFee={this.tournament?.z_fee}
              entryFeeIcon={
                <SkillzImage
                  source={
                    this._shouldShowZBCDiscount()
                      ? 'ic_cash_usd_background.png'
                      : this.getTournamentEntryFeeIcon()
                  }
                  style={styles.entryFeeCurrencyImage}
                  resizeMode={'contain'}
                />
              }
              discountedFeeIcon={
                this._shouldShowZBCDiscount() ? (
                  <SkillzImage
                    source={practiceCurrencyFullIconLargeURI}
                    style={styles.entryFeeCurrencyImage}
                    resizeMode={'contain'}
                  />
                ) : undefined
              }
            />
          </View>
        ) : (
          <View style={styles.currencyItemContainer}>
            <SkillzImage
              source={this.getTournamentEntryFeeIcon()}
              style={styles.entryFeeCurrencyImage}
              resizeMode={'contain'}
            />
            <Label style={styles.entryFeePrizeTextV2}>{this._getTournamentEntryFeeString()}</Label>
          </View>
        )}
      </View>
    );
  }

  _getStylesToUse(): Object {
    const adjustingStyles = {
      friendsTournamentAvatar: styles.friendsTournamentAvatar,
      friendsTournamentPlaceholderAvatar: styles.friendsTournamentPlaceholderAvatar,
      instaMatchAvatar: styles.instaMatchAvatar,
      matchContentContainer: styles.matchContentContainer,
      matchFirstRowContainer: styles.matchFirstRowContainer,
      matchRowsContainer: styles.matchRowsContainer,
      matchSecondRowContainer: styles.matchSecondRowContainer,
      playButton: styles.playButton,
      prizeContainer: styles.prizeContainer,
      prizeImage: styles.prizeImage,
      prizesDetailsView: styles.prizesDetailsView,
      instaMatchRowsContainer: styles.instaMatchRowsContainer,
      instaMatchSecondRowContainer: styles.instaMatchSecondRowContainer,
    };

    if (this.props.scaleToContainer) {
      this._convertPropertiesToScale(adjustingStyles);
    }

    return adjustingStyles;
  }

  _convertPropertiesToScale(stylePropsToConvert: {[key: string]: any}) {
    for (const key in stylePropsToConvert) {
      const scalingKey = 'scaling' + StringUtils.capitalizeFirstLetter(key);
      stylePropsToConvert[key] = styles[scalingKey as keyof typeof styles];
    }
  }

  _getTicketzString(tournament: Tournament): string {
    const totalTicketzPrize = _.get(tournament, 'total_ticketz_prize', 0);
    const tournamentTicketzPrize = _.get(tournament, 'ticketz_prize', 0);
    if (totalTicketzPrize > 0.0) {
      return I18n.abbreviatedNumber(totalTicketzPrize + tournamentTicketzPrize);
    }

    return I18n.abbreviatedNumber(tournamentTicketzPrize);
  }

  _playButtonTapped() {
    if (this.tournament.friend && this.state.tournamentExpired) {
      return;
    }

    if (this.props.displayOnly) {
      return;
    }

    // Track live bracket icon click from home screen
    if (this.tournament.isLiveBracket()) {
      trackTelemetryEvent({
        event: TelemetryEventName.LIVE_BRACKET_ICON_HOME_CLICK,
        payload: {
          ...LiveBracketTrackingHelper.getLiveBracketTrackingDataV2(this.tournament),
          currentState: LiveBracketTimerUtils.getCurrentLiveBracketState(this.tournament),
        },
      });
    }

    this._stopScalingAnimation();
    this._trackPlayButtonTapEvent();

    // Navigate to BracketsV2 screen for Live Bracket tournaments
    if (this.tournament.isLiveBracket()) {
      this._navigateToLiveBracket();
    } else {
      _.invoke(this, 'onPress', this.tournament);
    }
  }

  _navigateToLiveBracket() {
    const gameId = SkillzModule?.App?.GameId;
    const bracketId = this.tournament?.bracket?.id;

    if (!gameId) {
      return;
    }

    if (!bracketId) {
      return;
    }

    // Navigate to BracketsV2 screen with the bracket ID and tournament data
    const navigationUrl = `/games/${gameId}/brackets-v2`;
    const currentRound = this.tournament?.bracket?.current_round || 0;
    const navigationParams = {
      bracketId: bracketId.toString(),
      currentTab: currentRound > 0 ? 1 : 0, // Tab 1 if round > 0, else tab 0
      tournament: this.tournament, // Pass tournament data as serializable object
    };

    SkillzRouter.push(navigationUrl as any, navigationParams);
  }

  // Method is an exact copy of _playButtonTapped so we can override this method in PooledLiquidityCell
  _playGradientButtonTapped() {
    if (this.tournament.friend && this.state.tournamentExpired) {
      return;
    }

    if (this.props.displayOnly) {
      return;
    }

    // Track live bracket icon click from home screen
    if (this.tournament.isLiveBracket()) {
      trackTelemetryEvent({
        event: TelemetryEventName.LIVE_BRACKET_ICON_HOME_CLICK,
        payload: {
          ...LiveBracketTrackingHelper.getLiveBracketTrackingDataV2(this.tournament),
          currentState: LiveBracketTimerUtils.getCurrentLiveBracketState(this.tournament),
        },
      });
    }

    this._stopScalingAnimation();
    this._trackPlayButtonTapEvent();

    // Navigate to BracketsV2 screen for Live Bracket tournaments
    if (this.tournament.isLiveBracket()) {
      this._navigateToLiveBracket();
      return;
    }

    // For 3+ player H2H tournaments with the Client Prop on, bypass Versus screen and go right into the match
    if (
      this.tournament.isThreePlusH2H() &&
      ClientPropertyConstants.getThreePlusH2HStyleV2Enabled()
    ) {
      _.invoke(this, 'onPress', this.tournament, null, false, true);
    } else {
      _.invoke(this, 'onPress', this.tournament);
    }
  }

  _stopScalingAnimation() {
    this.state.scalingAnimation?.stop();
    this.setState({scalingAnimation: null});
  }

  _trackPlayButtonTapEvent() {
    const PLAY_EVENT = {
      name: PLAY_MATCH_SELECT,
      params: {
        id: this.tournament.id,
      },
    };

    if (this.tournament.friend) {
      AWSManager.trackEvents([PLAY_EVENT, {name: 'VS_FRIENDS_PLAY_JOIN'}]);
    } else {
      AWSManager.trackEvent(PLAY_MATCH_SELECT, PLAY_EVENT.params);
    }

    // Track Live Bracket Icon Home Click
    if (this.tournament.isLiveBracket()) {
      AWSManager.trackEvent(LIVE_BRACKET_ICON_HOME_CLICK, {
        result: this.liveBracketTrackingData || {}, // LiveBracketTrackingHelper removed
      });
    }
    trackTelemetryEvent({
      event: TelemetryEventName.LOBBY_PLAY_BUTTON_CLICK, // Track Live Bracket Icon Home Click
      includeUserWalletDetails: true,
      payload: {
        entryfee: this.tournament.cash_fee,
        total_prize_pool: this.tournament.total_cash_prize,
        number_of_players: this.tournament.max_players,
        contest_type: this.tournament.category_type,
        contest_id: this.tournament.id,
        contest_name: this.tournament.name,
        section_name: this.props.sectionHeader,
        recommended_template_slot: (this.props.cellIndex ?? 0) + 1,
      },
    });
  }

  _playButtonSetHighlighted(highlighted: boolean) {
    this.setState({playButtonHighlighted: highlighted});
  }

  _scrollToAndAnimateIfHighlighted() {
    if (this.props.displayOnly) {
      return;
    }

    const TEN_MILLISECONDS = 10;

    if (this._isHighlightCell() && !this.scrolled) {
      this.scrollToCellTimeout = setTimeout(() => {
        if (!_.get(this.props.matchSelectList, 'disableScrollToCell')) {
          const cellHandle = findNodeHandle(this.cell);
          if (cellHandle) {
            let headerHeight = Device.isTabletUI() ? 88 : 50; // eslint-disable-line no-magic-numbers
            if (this.tournament.category_type == 'LIVE') {
              const searchBarContainerHeight = 30;
              headerHeight += searchBarContainerHeight;
            }
            let sectionList = null;
            if (this.sectionList) {
              sectionList = this.sectionList();
            }
            const sectionIndex = this._getSectionIndex(sectionList, this.tournament);
            const itemIndex = this._getItemIndex(sectionList, sectionIndex);
            this.scrolled = true;
            if (sectionList) {
              try {
                sectionList.scrollToLocation({
                  sectionIndex: sectionIndex,
                  itemIndex: itemIndex,
                  animated: true,
                  viewOffset: headerHeight,
                });
              } catch (error) {
                console.debug('Android getItemLayout red screen error.');
              }
            }
          }
        }
        this.startScalingAnimation();
      }, TEN_MILLISECONDS);
    }
  }

  _getSectionIndex(sectionList: Object, tournament: Tournament): number {
    let sectionIndex = 0;

    if (sectionList && _.get(sectionList, 'props')) {
      _.forEach(sectionList.props.sections, (section: Object, index: number) => {
        if (tournament.category_type == section.type) {
          sectionIndex = index;
          return;
        }
      });
    }

    return sectionIndex;
  }

  _getItemIndex(sectionList: Object, sectionIndex: number): number {
    let itemIndex = 0;

    if (sectionList && _.get(sectionList, 'props')) {
      const section = sectionList.props.sections[sectionIndex];
      _.some(section.data, (obj: Tournament, index: number): boolean => {
        if (obj.featured || obj.isFocused) {
          itemIndex = index;
          return true;
        }
      });
    }
    return itemIndex;
  }

  startScalingAnimation() {
    this.stopScalingAnimation();
    if (this.hasScalingAnimation()) {
      if (!this.state.scalingAnimation && this._shouldBounce()) {
        const sequenceArray = [];
        sequenceArray.push(
          Animated.timing(this.bounceValue, {
            duration: 600,
            toValue: 0.975,
            useNativeDriver: true,
          })
        );
        sequenceArray.push(
          Animated.timing(this.bounceValue, {
            duration: 600,
            toValue: 1.0,
            useNativeDriver: true,
          })
        );
        const scalingAnimation = Animated.loop(Animated.sequence(sequenceArray));
        scalingAnimation.start();
        this.setState({scalingAnimation});
      }
    }
  }

  stopScalingAnimation() {
    this.state.scalingAnimation?.stop();
    this.setState({scalingAnimation: null});
  }
  // If tournament is Sponsored/Limited Use, we always have a banner and disregard any Featured Text
  // from server
  _renderFeaturedText(): React.ReactNode {
    const tournament = this.tournament;
    if (tournament?.force_user_to_play && ClientPropertyConstants.getFTUEV2FeatureEnabled()) {
      return null;
    }
    const limitedUseBannerBackgroundColor = featuredMatchBarColor.backgroundColor;
    const limitedUseBannerBorderColor = darkenColor(limitedUseBannerBackgroundColor);
    const colors = [limitedUseBannerBackgroundColor, limitedUseBannerBorderColor];
    const limitedUseBannerText = this._getLimitedUseTournamentBannerText();
    const isFTUEV2Enabled = ClientPropertyConstants.getFTUEV2FeatureEnabled();
    const showHighlightTextForFTUEV2 = isFTUEV2Enabled && !!tournament.highlight;
    if (tournament.isLimitedUseTournament() && limitedUseBannerText.length > 0) {
      if (showHighlightTextForFTUEV2) {
        return this._renderFeaturedTextV3(limitedUseBannerText);
      } else if (this.isTournamentTemplateUIV2) {
        return this._renderFeaturedTextV2(limitedUseBannerText);
      } else {
        return (
          <View style={styles.limitedUseBannerContainer}>
            <SkillzSVG
              source={'banner_limited_use_template.svg'}
              style={styles.limitedUseBanner}
              colors={colors}
              resizeMode={'stretch'}
            />
            <Label style={styles.limitedUseBannerText}>{limitedUseBannerText}</Label>
          </View>
        );
      }
    } else if (tournament.highlight_text) {
      if (showHighlightTextForFTUEV2) {
        return this._renderFeaturedTextV3(tournament.highlight_text);
      }
      if (this.isTournamentTemplateUIV2) {
        return this._renderFeaturedTextV2(tournament.highlight_text);
      }

      return (
        <View style={styles.featuredMatchBar}>
          <Label style={styles.featuredMatchBarText}>{tournament.highlight_text}</Label>
          {isFTUEV2Enabled && <ShimmerOverlay key={`shimmer-${this.tournament.id}`} />}
        </View>
      );
    }
    return null;
  }

  _renderFeaturedTextV2(bannerText: string): React.ReactNode {
    const limitedUseBannerBackgroundColor = featuredMatchBarColor.backgroundColor;
    const darkerGradient = darkenColor(limitedUseBannerBackgroundColor);
    const lighterGradient = lightenColor(limitedUseBannerBackgroundColor);
    const colors = [darkerGradient, lighterGradient];
    return (
      <View style={styles.limitedUseBannerContainerV2}>
        <LinearGradient colors={colors} style={styles.limitedUseBannerV2}>
          <Label style={styles.limitedUseBannerTextV2}>{bannerText.toUpperCase()}</Label>
        </LinearGradient>
      </View>
    );
  }

  _renderFeaturedTextV3(bannerText: string): React.ReactNode {
    const colors = ['#FBE99D', '#D8984C'];
    const locations = [0.0092, 0.9973];
    const start = {x: 0, y: 0.5};
    const end = {x: 1, y: 0.5};
    return (
      <View style={styles.limitedUseBannerContainerV2}>
        <LinearGradient
          colors={colors}
          style={styles.limitedUseBannerV3}
          locations={locations}
          start={start}
          end={end}
        >
          <Label style={styles.limitedUseBannerTextV3}>{bannerText}</Label>
        </LinearGradient>
      </View>
    );
  }

  _getLimitedUseTournamentDefaultBannerText(): string {
    const tournament = this.tournament;
    const limitedUseType = tournament.getLimitedUseTournamentType();
    const tournamentEntries = this.state.limitedUseEntriesRemaining;

    if (limitedUseType === 'ENTRY_PLAYER' && tournamentEntries) {
      return _m('Entries Available: {0}', tournamentEntries);
    }
    if (limitedUseType === 'ENTRY_TEMPLATE' && tournamentEntries) {
      return _m('Open Matches Left: {0}', tournamentEntries);
    }
    if (limitedUseType === 'TIME') {
      return _m('Time Left: {0}', this.state.endsIn);
    }
    return '';
  }

  _getLimitedUseTournamentBannerText(): string {
    const tournament = this.tournament;
    if (!tournament.isLimitedUseTournament()) {
      return '';
    }

    if (tournament.getLimitedUseTournamentBannerType() === 'DEFAULT') {
      return this._getLimitedUseTournamentDefaultBannerText();
    }

    if (tournament.getLimitedUseTournamentBannerType() === 'LIMITED_TIME') {
      return _m('Limited Time Match!');
    }

    if (tournament.getLimitedUseTournamentBannerType() === 'SPECIAL_LIMITED_TIME') {
      return _m('Special Offer!');
    }

    if (
      tournament.getLimitedUseTournamentBannerType() === 'CUSTOM' &&
      tournament.hasLimitedUseTournamentCustomBannerText()
    ) {
      /**
       * On small screens, the Limited Use banner can only hold ~25 characters. Need to truncate
       * the text b/c the banner and text are using ```position: absolute``` and therefore we
       * cannot restrict the width of the text container to let the Text component truncate for us.
       */
      if (!this.isTournamentTemplateUIV2) {
        return _.truncate(tournament.limited_use?.banner?.custom_text || '', {length: 25});
      } else {
        return tournament.limited_use?.banner?.custom_text || '';
      }
    }

    return '';
  }

  _getLiveBracketTimerText(): string {
    return LiveBracketTimerUtils.getLiveBracketTimerText(this.tournament, this.state.timeUntil);
  }

  _textForDescription(): Array<string> {
    const tournament = this.tournament;
    const descriptionText = [];

    if (tournament && (tournament.category_type === 'LIVE' || tournament.isLiveBracket())) {
      if (tournament.name != tournament.live_event?.name) {
        descriptionText.push(tournament.live_event?.name || '');
      }
      let timeDetailText = '';
      if (!this.tournament.hasTournamentStarted()) {
        if (this.state.timeUntil) {
          // For live brackets, use new timer logic
          if (tournament.isLiveBracket()) {
            timeDetailText = this._getLiveBracketTimerText();
          } else {
            timeDetailText = _m('Begins In:') + this.state.timeUntil;
          }
        }
      } else {
        if (tournament.isPlayerWaitingForNextRound()) {
          timeDetailText = _m("Waiting on rivals' rounds to end!");
        } else if (tournament.bracket) {
          if (tournament.isPlayerRegistered() && tournament.isPlayerMatchReadyToPlay()) {
            timeDetailText = _m('Match Expires in {0}', this.state.matchAbortedIn);
          } else {
            timeDetailText = _m('Tournament In Progress');
          }
        } else {
          if (this.state.endsIn) {
            // For live brackets, format the timer
            if (tournament.isLiveBracket()) {
              const formattedTime = LiveBracketTimerUtils.formatLiveBracketTimer(this.state.endsIn);
              timeDetailText = _m('Ends In:') + ' ' + formattedTime;
            } else {
              timeDetailText = _m('Ends In:') + ' ' + this.state.endsIn;
            }
          }
        }
      }
      descriptionText.push(timeDetailText);
    } else if (tournament && tournament.category_type === 'BRACKET') {
      const secondsAsDays = I18n.humanReadableIntervalAsInteger(
        tournament.bracket?.round_length_seconds || 0
      );
      descriptionText.push(_m('Round Time') + ': ' + secondsAsDays);
    } else if (tournament && tournament.friend) {
      if (tournament.end) {
        if (this.state.endsIn) {
          descriptionText.push(_m('Ends:') + ' ' + this.state.endsIn);
        }
      } else if (
        (tournament.isOffSystemFTTemplate() && tournament.getCurrentChallengeCount() === 0) ||
        (!tournament.isOffSystemFTTemplate() &&
          tournament.isIncentiveTournament() &&
          !tournament.hasMaxChallenges())
      ) {
        descriptionText.push(
          _m('New Challenge in {0}', I18n._getTimeRemainingFromSeconds(this.state.ftResetIn || 0))
        );
      } else if (tournament.isOffSystemFTTemplate() && !tournament.hasUnlimitedChallenges()) {
        descriptionText.push(
          _m(
            '{0}/{1} Challenges Available',
            tournament.getCurrentChallengeCount(),
            tournament.getMaxChallengeCountNumber()
          )
        );
      }
    } else if (
      tournament &&
      tournament.description &&
      tournament.description.length &&
      tournament.description !== 'No description'
    ) {
      if (tournament.isIncomingInvite() || !tournament.isFriendTournament()) {
        descriptionText.push(tournament.description);
      }
    }

    return descriptionText;
  }

  _getTournamentName(): string {
    const tournament = this.tournament;

    if (this.props.mainText) {
      return _m(this.props.mainText);
    } else if (tournament.isFriendTournament()) {
      if (tournament.isIncomingInvite()) {
        const username = _.get(tournament, 'creator.display_name', this.state.currentUser.username);
        const contactName = NativeContactsManager.getUserContactName(username);
        const displayName = contactName || username;
        return _m("{0}'s Tournament", displayName);
      } else if (tournament.isOffSystemFTTemplate()) {
        return ClientPropertyConstants.getFriendsTournamentsOffsystemFriendsTileCopy();
      } else {
        if (tournament.isIncentiveTournament()) {
          return _m(
            '{0}/{1} Challenges Available',
            tournament.getCurrentChallengeCount(),
            tournament.getMaxChallengeCountNumber()
          );
        }
        try {
          return decodeURI(tournament.name);
        } catch (error) {
          console.debug(error);
          return decodeURI(_m('eSports Tournament'));
        }
      }
    } else {
      try {
        return decodeURI(tournament.name);
      } catch (error) {
        console.debug(error);
        return decodeURI(_m('eSports Tournament'));
      }
    }
  }

  _renderDescriptionCopy(): React.ReactNode {
    const tournament = this.tournament;
    const descriptionValues = [];
    const tournamentName = this._getTournamentName();
    const matchTitleText = this.isTournamentTemplateUIV2
      ? styles.matchTitleTextV2
      : styles.matchTitleText;
    const syncTemplateStyle = this.props.isSync ? {marginTop: 0} : null;

    if (tournamentName?.length > 15 && Sizes.isTablet() && this.props.isFriendsTournament) {
      descriptionValues.push(
        <Label
          key={tournament.name + '-' + tournament.id}
          minimumFontScale={0.1}
          adjustsFontSizeToFit={true}
          numberOfLines={2}
          testID={TEST_IDS.MATCH_SELECT_CELL_DESCRIPTION}
          style={[matchTitleText, {...fontSizeH3}, syncTemplateStyle]}
        >
          {tournamentName}
        </Label>
      );
    } else if (this._shouldRenderInstaMatch()) {
      // If Instant Match, add player count to top line because there is no room in Prizes section anymore
      descriptionValues.push(
        <View style={styles.matchTitleTextContainerInstaMatch}>
          <Optional test={!this.isTournamentTemplateUIV2}>
            <View style={[styles.numPlayerText, styles.numPlayerTextInstaMatch]}>
              {this._renderPlayers()}
            </View>
          </Optional>
          <Optional test={!this.isTournamentTemplateUIV2}>
            <View style={[styles.verticalViewSeperator, styles.verticalViewSeperatorInstaMatch]} />
          </Optional>
          <Label
            key={tournament.name + '-' + tournament.id}
            minimumFontScale={0.1}
            testID={TEST_IDS.MATCH_SELECT_CELL_DESCRIPTION}
            numberOfLines={
              ClientPropertyConstants.getLiveEventHostedEnabled() &&
              this.tournament.isBrandedTournament()
                ? 2
                : 1
            }
            style={[matchTitleText]}
          >
            {tournamentName}
          </Label>
        </View>
      );
    } else {
      descriptionValues.push(
        <Label
          key={tournament.name + '-' + tournament.id}
          minimumFontScale={0.1}
          testID={TEST_IDS.MATCH_SELECT_CELL_DESCRIPTION}
          numberOfLines={
            ClientPropertyConstants.getLiveEventHostedEnabled() &&
            this.tournament.isBrandedTournament()
              ? 2
              : 1
          }
          style={[matchTitleText, syncTemplateStyle]}
        >
          {tournamentName}
        </Label>
      );
    }

    const descriptionText = this._textForDescription();

    if (Array.isArray(descriptionText) && !this.props.mainText) {
      descriptionText.forEach((description: string, index: number) => {
        const marginTop = index == 0 ? -1 : 0; // eslint-disable-line no-magic-numbers
        const marginBottom = index == 0 ? -1 : 0; // eslint-disable-line no-magic-numbers
        const dynamicStyle = {
          marginBottom: marginBottom,
          marginTop: marginTop,
        };
        const descriptionStyles = this.isTournamentTemplateUIV2
          ? styles.matchDescriptionTextV2
          : styles.matchDescriptionText;
        if (typeof description === 'string') {
          // Check if this is timer text for live brackets and render with separate components
          const isTimerText = LiveBracketTimerUtils.shouldApplyMixedColors(description, tournament);

          if (isTimerText) {
            // Split the text to apply different colors
            const {prefix, timePart} = LiveBracketTimerUtils.splitTimerText(description);

            // Create two separate Text components with flexible layout
            descriptionValues.push(
              <View
                key={description + '-' + tournament.id + '-' + index}
                style={[dynamicStyle, {flexDirection: 'row', flexWrap: 'wrap'}]}
              >
                <Text
                  adjustsFontSizeToFit={this.isTournamentTemplateUIV2 ? false : true}
                  allowFontScaling={false}
                  minimumFontScale={0.1}
                  numberOfLines={this.isTournamentTemplateUIV2 ? 2 : 1}
                  testID={TEST_IDS.MATCH_SELECT_CELL_DESCRIPTION}
                  style={[descriptionStyles, styles.matchDescriptionTextPrefix]}
                >
                  {prefix}
                </Text>
                <Text
                  adjustsFontSizeToFit={this.isTournamentTemplateUIV2 ? false : true}
                  allowFontScaling={false}
                  minimumFontScale={0.1}
                  numberOfLines={this.isTournamentTemplateUIV2 ? 2 : 1}
                  testID={TEST_IDS.MATCH_SELECT_CELL_DESCRIPTION}
                  style={[descriptionStyles, styles.matchDescriptionTextTimePart]}
                >
                  {timePart}
                </Text>
              </View>
            );
          } else {
            descriptionValues.push(
              <Label
                adjustsFontSizeToFit={this.isTournamentTemplateUIV2 ? false : true}
                allowFontScaling={false}
                key={description + '-' + tournament.id + '-' + index}
                minimumFontScale={0.1}
                numberOfLines={this.isTournamentTemplateUIV2 ? 2 : 1}
                testID={TEST_IDS.MATCH_SELECT_CELL_DESCRIPTION}
                style={[descriptionStyles, dynamicStyle]}
              >
                {this._getDescriptionName(description)}
              </Label>
            );
          }
        } else {
          descriptionValues.push(description);
        }
      });
    }

    return descriptionValues;
  }

  _getDescriptionName(description: string): string {
    try {
      return decodeURI(description);
    } catch {
      return decodeURI(_m('Compete for Prizes'));
    }
  }

  _getTileAccessibilityString(): string {
    if (this.tournament) {
      let prefix = '';
      if (this.tournament.isFriendTournament()) {
        if (this.tournament.isIncomingInvite()) {
          const username = _.get(
            this.tournament,
            'creator.display_name',
            this.state.currentUser.username
          );
          const contactName = NativeContactsManager.getUserContactName(username);
          const displayName = contactName || username;
          prefix = this.props.sectionHeader + '-Invite-' + displayName;
        } else {
          prefix = this.props.sectionHeader + '-' + this.tournament.name;
        }
      } else {
        prefix = this.props.sectionHeader + '-' + this.tournament.name;
      }
      return prefix;
    }

    return '';
  }

  _getPrizesAccessibilityString(): string {
    if (this.tournament) {
      const medalAmount = this.tournament.isPointsTournament()
        ? this.tournament.total_z_prize
        : this.tournament.total_cash_prize;
      const prefix = this._getTileAccessibilityString();
      return (
        prefix +
        '-Prizes-' +
        this.tournament.formatted_prize +
        '-' +
        this.tournament.ticketz_prize +
        't-' +
        medalAmount +
        'm'
      );
    }

    return '';
  }

  _renderTicketzPrize(): React.JSX.Element {
    const matchSubtitleText = styles.matchSubtitleText;
    return (
      <View style={styles.ticketzPrizeView}>
        <SkillzImage source={'ic_ticketz_background.png'} style={styles.ticketzImage} />
        <Label
          adjustsFontSizeToFit={false}
          style={[styles.matchSubtitleText, this._getFontSize(), textColorTicketz]}
        >
          {this.state.ticketzString}
        </Label>
      </View>
    );
  }

  _renderPlusSign(): React.JSX.Element {
    const extraStyle = this.isTournamentTemplateUIV2 ? {marginHorizontal: 1} : {};
    return (
      <Label
        adjustsFontSizeToFit={false}
        style={[styles.matchSubtitleText, this._getFontSize(), styles.prizesPlusText, extraStyle]}
        textAlign={'center'}
      >
        {'+'}
      </Label>
    );
  }

  _renderPrizesText(): React.JSX.Element | null {
    const currencyStyle = this.tournament.isCashTournament()
      ? textColorCash
      : this.tournament.isPointsTournament()
        ? textColorZ
        : {};

    const prizeValue = this.tournament.getTopPrize();
    const prize = this.tournament.isPointsTournament()
      ? I18n.getIntegerString(prizeValue)
      : I18n.getCurrencyString(prizeValue, '$', true, true);
    const prizeText = this.isTournamentTemplateUIV2 ? '' : _m('PRIZES ');

    const prizeLabelStyle = [styles.matchSubtitleText, this._getFontSize()];
    const prizesLabel = (
      <Label adjustsFontSizeToFit={false} style={prizeLabelStyle}>
        {prizeText}
      </Label>
    );

    if (this.tournament.isTicketzTournament()) {
      return prizesLabel;
    } else if (this.tournament.isPointsTournament() && !this.isTournamentTemplateUIV2) {
      return (
        <View style={styles.prizesTextView}>
          <Label adjustsFontSizeToFit={false} style={prizeLabelStyle}>
            {prizeText + SkillzStyle.currencyValues.practiceCurrencyString + prize}
          </Label>
        </View>
      );
    } else if (this.tournament.isLiveBracket()) {
      if (prizeValue <= 0) {
        return null;
      }

      return (
        <View style={styles.prizesTextView}>
          {prizesLabel}
          <Label
            adjustsFontSizeToFit={false}
            style={[styles.matchSubtitleText, this._getFontSize(), textColorCash]}
          >
            {prize}
          </Label>
        </View>
      );
    } else if (
      this.tournament.isCustomPrizeTournament() &&
      ClientPropertyConstants.getLiveEventHostedEnabled() &&
      this.tournament.estimated_total_value > 0
    ) {
      return (
        <View style={styles.prizesTextView}>
          {prizesLabel}
          <Label
            adjustsFontSizeToFit={false}
            style={[styles.matchSubtitleText, this._getFontSize(), textColorCash]}
          >
            {I18n.getCurrencyString(this.tournament.estimated_total_value, '$', true, true)}
          </Label>
        </View>
      );
    } else if (this.tournament.isCustomPrizeTournament()) {
      return (
        <View style={styles.prizesTextView}>
          {prizesLabel}
          <SkillzImage
            source={'ic_leaderboard_custom_prize_skz.png'}
            style={styles.customPrizeIconImage}
          />
        </View>
      );
    }

    if (prizeValue <= 0) {
      return null;
    }

    if (this.isTournamentTemplateUIV2) {
      const imageSource = this.tournament.isPointsTournament()
        ? practiceCurrencyFullIconLargeURI
        : 'ic_cash_usd_background.png';
      return (
        <View style={styles.ticketzPrizeView}>
          <SkillzImage source={imageSource} style={styles.ticketzImage} />
          <Label style={[styles.matchSubtitleText, this._getFontSize(), currencyStyle]}>
            {I18n.getCurrencyString(prizeValue, '', true, true)}
          </Label>
        </View>
      );
    }

    return (
      <View style={styles.prizesTextView}>
        {prizesLabel}
        <Label
          adjustsFontSizeToFit={false}
          style={[styles.matchSubtitleText, this._getFontSize(), currencyStyle]}
        >
          {prize}
        </Label>
      </View>
    );
  }

  getTournamentEntryFeeIcon() {
    //Entry Fee can be either cash, z. It is free if both are 0.
    if (
      this.tournament.category_type === 'LIVE' &&
      this.tournament.cash_fee > 0.0 &&
      (this.tournament.hasFreeEntriesRemaining?.() || this.tournament.hasVideoAdEntriesRemaining())
    ) {
      return undefined;
    } else if (this.tournament.cash_fee > 0.0) {
      return 'ic_cash_usd_background.png';
    } else if (this.tournament.z_fee > 0.0) {
      return practiceCurrencyFullIconLargeURI;
    }
    return undefined;
  }

  _getTournamentCreatorAndInstaMatchStyle() {
    if (this.props.isTournamentCreator) {
      return styles.tournamentCreatorPrizesDetailsView;
    }
    if (this._shouldRenderInstaMatchDiscounted(this.tournament)) {
      return styles.prizesDetailsViewInstaMatchDiscount;
    }
    if (this._shouldRenderInstaMatch()) {
      return styles.prizesDetailsViewInstaMatchNoDiscount;
    }
    return {};
  }

  _renderPrizes(): React.ReactNode {
    const syncStyle = this.props.isSync ? styles.syncSearchingFlex : {};

    const tournamentCreatorAndInstaMatchStyle = this._getTournamentCreatorAndInstaMatchStyle();
    const medalAmount = ClientPropertyConstants.getLeaguesCombinedMedalsEnabled()
      ? this.tournament.getCombinedMedalsForMatchSelectCell()
      : this.tournament.isPointsTournament()
        ? this.tournament.total_z_prize
        : this.tournament.isZEntryBonusCashPrizeTournament()
          ? this.tournament.total_bonus_cash_prize
          : this.tournament.total_cash_prize;
    const showMedals = !(
      this.tournament.isFriendTournament() ||
      (Tournament.determineTournamentCategory(this.tournament) === 'LIVE' &&
        !ClientPropertyConstants.getLeaguesCombinedMedalsEnabled())
    );
    const prizesText = this._renderPrizesText();

    return (
      <View
        accessible={true}
        accessibilityLabel={this._getPrizesAccessibilityString()}
        testID={TEST_IDS.MATCH_SELECT_CELL_PRIZES}
        style={[
          this.adjustingStyles.prizesDetailsView,
          syncStyle,
          tournamentCreatorAndInstaMatchStyle,
        ]}
      >
        {prizesText}
        <Optional
          test={
            !this.tournament.isTicketzTournament() &&
            this.tournament.ticketz_prize > 0 &&
            prizesText
          }
        >
          {this._renderPlusSign()}
        </Optional>
        <Optional test={this.tournament.ticketz_prize > 0}>{this._renderTicketzPrize()}</Optional>
        <Optional test={medalAmount > 0 && showMedals}>{this._renderMedals(medalAmount)}</Optional>
      </View>
    );
  }

  _renderMedals(medalAmount: number): React.JSX.Element {
    if (!medalAmount || medalAmount < 1) {
      return <React.Fragment />;
    }

    const medalIconName = getMedalIcon(this.tournament.isCashTournament());

    return (
      <View style={styles.prizesTextView}>
        {this._renderPlusSign()}
        <Optional test={!ClientPropertyConstants.getLeaguesCombinedMedalsEnabled()}>
          <SkillzImage
            resizeMode={'contain'}
            source={{uri: medalIconName}}
            style={styles.medalImage}
          />
        </Optional>
        <Optional test={ClientPropertyConstants.getLeaguesCombinedMedalsEnabled()}>
          <View style={{marginRight: this.isTournamentTemplateUIV2 ? 1 : 0.5}}>
            <LeagueMedal
              medal={ELeagueMedal.UNIFIED}
              styleOverwrite={{size: styles.medalImage.height}}
            />
          </View>
        </Optional>
        <Label adjustsFontSizeToFit={false} style={[styles.matchSubtitleText, this._getFontSize()]}>
          {String(I18n.abbreviatedNumber(medalAmount) || '')}
        </Label>
      </View>
    );
  }

  _matchPlayersText(): string {
    let playersText = '';
    const tournament = this.tournament;

    if (this.props.hasMinimumPlayers) {
      /* While this is returning tournament.max_players, this string actually
       * represents the minimum number of players required for the live event.
       * In order to reuse this component, SKZLiveEventTemplate.minimum_unique_participants
       * was converted into SKZTournament.max_players */
      return I18n.abbreviatedNumber(tournament.max_players) + '+';
    }

    if (tournament.category_type == 'BRACKET') {
      playersText = String(tournament.bracket?.size || ''); // ✅ FIX: Convert number to string for Label component + handle undefined
    } else if (tournament.live_event) {
      if (tournament.bracket) {
        playersText = tournament.getTextForBracketRegistrationPlayerCount();
      } else {
        if (tournament.max_players >= ONE_BILLION) {
          playersText = '\u221e';
        } else {
          playersText = I18n.abbreviatedNumber(tournament.max_players);
        }
      }
    } else {
      playersText = I18n.abbreviatedNumber(tournament.max_players);
    }

    return playersText;
  }

  _renderPlayers(): React.ReactNode {
    const tournament = this.tournament;
    const registrationStartTime = tournament?.bracket?.register_start_time;

    // Donot render registered players text if the registration has not started
    if (
      tournament?.isLiveBracket() &&
      !LiveBracketTimerUtils.hasLiveBracketRegistrationStarted(registrationStartTime)
    ) {
      return null;
    }

    const playersText = this._matchPlayersText();

    const styleAdjustmentForEventFullText: TextStyle =
      tournament.isBracketFull() && !this.isTournamentTemplateUIV2 ? fontSizeH6 : {};

    const playerView = (
      <View
        style={[styles.ticketzPrizeView, SkillzStyle.fullStyles.px2]}
        testID={TEST_IDS.MATCH_SELECT_CELL_PLAYERS}
      >
        <SkillzImage
          key={'player_logo'}
          source={
            this.isTournamentTemplateUIV2 ? 'ic_player_icon_skz_v2.svg' : 'ic_player_icon_skz.png'
          }
          style={styles.playerIconImage}
        />
        <Label
          adjustsFontSizeToFit={true}
          key={playersText + '-' + tournament.id}
          minimumFontScale={0.01}
          style={[styles.matchPlayersText, this._getFontSize(), styleAdjustmentForEventFullText]}
        >
          {playersText}
        </Label>
      </View>
    );
    if (this.isTournamentTemplateUIV2) {
      return (
        <LinearGradient colors={['#DDDAD6', '#FAF6F1']} style={styles.playersView}>
          {playerView}
        </LinearGradient>
      );
    } else {
      return playerView;
    }
  }

  _renderMatchDetailsRow(): React.ReactNode {
    const tournament = this.tournament;
    const adjustmentForEventFull = tournament.isBracketFull()
      ? {
          flex: 0.8,
        }
      : {};

    // If Instant Match, don't render players count
    if (tournament.type && this._shouldRenderInstaMatch()) {
      return (
        <View
          style={this.adjustingStyles.matchSecondRowContainer}
          testID={TEST_IDS.MATCH_SELECT_CELL_DETAILS}
        >
          {this._renderPrizes()}
          <Optional test={this.isTournamentTemplateUIV2}>
            <View style={[styles.numPlayerText, adjustmentForEventFull]}>
              {this._renderPlayers()}
            </View>
          </Optional>
          <Optional test={!this.isTournamentTemplateUIV2}>
            <View style={styles.verticalViewSeperator} />
          </Optional>
          <Optional test={!this.isTournamentTemplateUIV2}>{this._getEntryFeeView()}</Optional>
        </View>
      );
    }

    if (tournament.type) {
      return (
        <View
          style={this.adjustingStyles.matchSecondRowContainer}
          testID={TEST_IDS.MATCH_SELECT_CELL_DETAILS}
        >
          {this._renderPrizes()}
          <Optional test={!this.isTournamentTemplateUIV2}>
            <View style={styles.verticalViewSeperator} />
          </Optional>
          <View style={[styles.numPlayerText, adjustmentForEventFull]}>
            {this._renderPlayers()}
          </View>
          <Optional test={!this.isTournamentTemplateUIV2}>
            <View style={styles.verticalViewSeperator} />
          </Optional>
          <Optional test={!this.isTournamentTemplateUIV2}>{this._getEntryFeeView()}</Optional>
        </View>
      );
    }
  }

  _updateEventTimes() {
    const tournament = this.tournament;

    if (tournament.live_event || tournament.isLiveBracket()) {
      if (tournament.isPlayerMatchReadyToPlay()) {
        this.setState({
          matchAbortedIn: I18n.daysHrsMinsSecsUntil(tournament.bracket?.user_expires_at),
        });
      }
      this._updateTimeUntil();
      this._updateEndsIn();
    }
  }

  _updateLimitedUseTimer() {
    const endsInString = this.tournament.getLimitedUseTournamentEndsInString('');

    if (endsInString.length > 0) {
      this.setState({
        endsIn: endsInString,
      });
    } else {
      if (this.state.endsIn != _m('Expired')) {
        PlayableMatchesActionCreator.fetchMatchList(false);
        this.setState({
          endsIn: _m('Expired'),
        });
      }
    }
  }

  _updateTimeUntil() {
    const tournament = this.tournament;

    if (this.state.timeUntil && tournament.hasTournamentStarted()) {
      PlayableMatchesActionCreator.fetchMatchList(false);
      this.setState({
        timeUntil: null,
      });
    } else if (!tournament.hasTournamentStarted()) {
      // For live brackets, use the appropriate time based on current phase
      if (tournament.isLiveBracket() && tournament.bracket) {
        const now = moment();

        // Use utility function to validate bracket timing moments
        const {
          showStartTime,
          registerStartTime,
          registerEndTime,
          isValid: areMomentsValid,
        } = LiveBracketTimerUtils.validateBracketTimingMoments(
          tournament.bracket.show_start_time,
          tournament.bracket.register_start_time,
          tournament.bracket.register_end_time
        );

        // If any of the bracket times are invalid, fall back to regular tournament logic
        if (!areMomentsValid) {
          this.setState({
            timeUntil: I18n.daysHrsMinsSecsUntil(tournament.start),
          });
          return;
        }

        let targetTime;

        if (now.isBefore(showStartTime)) {
          // Before show_start_time: show time until register_start_time
          targetTime = registerStartTime.toDate().toISOString();
        } else if (now.isBetween(showStartTime, registerStartTime)) {
          // Between show_start_time and register_start_time: show time until register_start_time
          targetTime = registerStartTime.toDate().toISOString();
        } else if (now.isBetween(registerStartTime, registerEndTime)) {
          // Between register_start_time and register_end_time: show time until register_end_time
          targetTime = registerEndTime.toDate().toISOString();
        } else {
          // After register_end_time: no timer needed
          targetTime = null;
        }

        if (targetTime) {
          this.setState({
            timeUntil: I18n.daysHrsMinsSecsUntil(targetTime),
          });
        }
      } else {
        // For regular tournaments, use the original logic
        this.setState({
          timeUntil: I18n.daysHrsMinsSecsUntil(tournament.start),
        });
      }
    }
  }

  _updateEndsIn() {
    const {shouldUpdateEndsIn} = this.props;
    const tournament = this.tournament;
    let endsInString = '';

    // Handle live brackets with register_end_time
    if (tournament.isLiveBracket() && tournament.bracket?.register_end_time) {
      const now = moment();
      const registerEndTime = moment(tournament.bracket.register_end_time);

      // If registration has ended and user is not registered, don't show timer
      if (now.isAfter(registerEndTime) && !tournament.isPlayerRegistered()) {
        endsInString = '';
      } else {
        // Show time until registration ends or tournament ends
        const endTime = tournament.bracket.bracket_end_time || tournament.bracket.register_end_time;
        endsInString = I18n.daysHrsMinsSecsUntil(endTime);
      }
    } else {
      // Handle regular live events
      endsInString = tournament.getLiveEventEndsInString('');
    }

    if (endsInString.length > 0) {
      this.setState({
        endsIn: endsInString,
      });
    } else {
      if (shouldUpdateEndsIn && this.state.endsIn != _m('Expired')) {
        PlayableMatchesActionCreator.fetchMatchList(false);
        this.setState({
          endsIn: _m('Expired'),
        });
      }
    }
  }

  _renderPlayButtonOverlay(): React.ReactNode | null {
    const tournament = this.tournament;
    const buttonText = tournament.textForPlayButton();

    if (buttonText) {
      return (
        <View
          style={styles.playButtonOverlayContainer}
          testID={TEST_IDS.MATCH_SELECT_CELL_PLAY_BUTTON}
        >
          <Label key={buttonText + '-' + tournament.id} style={styles.playButtonOverlayText}>
            {buttonText}
          </Label>
        </View>
      );
    }

    return null;
  }

  _renderInFlightEventButtonContent(tournament: Tournament): React.JSX.Element {
    const hostedTournamentCustomColor = this.tournament.getHostedTournamentCustomColor();

    const hasHostedTournamentCustomColor =
      ClientPropertyConstants.getLiveEventHostedEnabled() && this.tournament.hasBrandedColor();

    const playGradientButtonColor = hasHostedTournamentCustomColor
      ? [hostedTournamentCustomColor, hostedTournamentCustomColor]
      : playButtonGradient;

    // Returns a different play button for: Live brackets on "Ready to play" state
    if (
      tournament.isLiveBracket() &&
      tournament.isPlayerRegistered() &&
      tournament.hasTournamentStarted() &&
      tournament.isPlayerMatchReadyToPlay()
    ) {
      return <LiveBracketsReadyToPlayButton tournament={tournament} />;
    }

    // Returns a different play button for: Live brackets on "Next round coming soon" state
    if (tournament.isPlayerWaitingForNextRound()) {
      return <LiveBracketsButton tournament={tournament} />;
    }
    return (
      <PlayGradientButton
        colors={playGradientButtonColor}
        onPress={() => {
          this._playGradientButtonTapped();
        }}
        style={this.adjustingStyles.playButton}
      >
        {this._renderPlayButtonOverlay()}
      </PlayGradientButton>
    );
  }

  _renderUpcomingEventButtonContent(tournament: Tournament): React.JSX.Element {
    if (this.isTournamentTemplateUIV2) {
      return null;
    }
    // Returns a different play button for upcoming Live brackets
    if (tournament.isLiveBracket()) {
      const isJoinNowState =
        tournament.isLiveBracket() &&
        !tournament.isPlayerRegistered() &&
        !tournament.isBracketFull();

      return <LiveBracketsButton tournament={tournament} renderWithPlayButton={isJoinNowState} />;
    }

    // default content to upcoming event button
    return (
      <View style={this.adjustingStyles.playButton}>
        <SkillzImage
          source={'ic_match_select_not_started_skz.png'}
          style={StyleSheet.absoluteFill}
        />
        {this._renderPlayButtonOverlay()}
      </View>
    );
  }

  _renderUpcomingEventButton(): React.ReactNode {
    const tournament = this.tournament;

    if (!tournament.live_event || this.props.displayOnly || tournament.hasTournamentStarted()) {
      return <React.Fragment />;
    }

    return (
      <View style={styles.upcomingEventOverlay}>
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={(): void => this._playButtonTapped()}
          style={[styles.endCap, styles.upcomingEventButton]}
          accessibilityLabel={createAccessibilityLabel('UpcomingEventButton', {
            context: this.props.sectionHeader,
            index: this.props.cellIndex,
          })}
        >
          {this._renderUpcomingEventButtonContent(tournament)}
        </TouchableOpacity>
      </View>
    );
  }

  _renderPrizeAsTextV2(): React.ReactNode {
    // Calculate the font size based on the length of the prize string
    const prizeLength = this.state.prizeString.length;
    const practiceCurrencyString = SkillzStyle.currencyValues.practiceCurrencyString;
    const isPoints = this.tournament.isPointsTournament();
    const isTicketzTournament = this.tournament.isTicketzTournament();
    const showZCoin = isPoints && !this.tournament.isLiveBracket();
    const fontSize = dynamicFontSize(showZCoin ? prizeLength + 1 : prizeLength);
    const currencyHeight = PRIZE_BACKGROUND_HEIGHT / 2;
    const prizePoolHeight = PRIZE_BACKGROUND_HEIGHT / 4;
    const practiceIconFontSize = fontSize / 2 - 2;

    // Calculate the vertical adjustment needed for the practice currency icon
    const verticalAdjustment = (fontSize - practiceIconFontSize) / 2;

    //The width of the SVG  for Z prize icon is based on the font size plus the stroke width. The stroke width of 6 will make sure the icons are not cut off.
    const svgWidth = practiceIconFontSize + 6;
    //Correction unit is used to adjust the width of the prize text based on the length of the prize string to make sure the text is not cut off.
    const correctionUnit = prizeLength == 1 ? 10 : prizeLength > 2 ? 3 : 5;
    const heavyWeight = Platform.OS !== 'web' ? {fontWeight: '800'} : {};
    const mediumWeight = Platform.OS !== 'web' ? {fontWeight: '700'} : {};
    const prizeWidth =
      showZCoin || isTicketzTournament
        ? prizeLength * practiceIconFontSize + correctionUnit * prizeLength
        : PRIZE_BACKGROUND_WIDTH;

    const prizeTextY =
      Platform.OS === 'ios' ? currencyHeight - Sizes.dynamic({sm: 5, xxlg: 8}) : currencyHeight - 6;

    return (
      <View style={styles.svgMainContainer}>
        <View style={styles.svgContainer}>
          <Optional test={showZCoin}>
            <Svg height={currencyHeight} width={svgWidth}>
              <SVGText
                stroke='#2A2A2A'
                strokeOpacity={0.6}
                fontSize={practiceIconFontSize}
                fontFamily={fontSecondary.fontFamily}
                {...heavyWeight}
                x={svgWidth / 2}
                y={currencyHeight - verticalAdjustment}
                textAnchor='middle'
                strokeWidth={6}
              >
                {practiceCurrencyString}
              </SVGText>

              <SVGText
                fill='#FFFFFF'
                fontSize={practiceIconFontSize}
                fontFamily={fontSecondary.fontFamily}
                {...heavyWeight}
                x={svgWidth / 2}
                y={currencyHeight - verticalAdjustment}
                textAnchor='middle'
              >
                {practiceCurrencyString}
              </SVGText>
            </Svg>
          </Optional>
          <Optional test={isTicketzTournament}>
            <SkillzImage
              source={'ic_ticketz_background.png'}
              style={[styles.ticketzPrizeImage, {top: currencyHeight / 6}]}
            />
          </Optional>
          <Svg height={currencyHeight} width={prizeWidth}>
            <SVGText
              stroke='#2A2A2A'
              strokeOpacity={0.6}
              fontSize={fontSize}
              fontFamily={fontSecondary.fontFamily}
              {...heavyWeight}
              x={showZCoin || isTicketzTournament ? 2 : PRIZE_BACKGROUND_WIDTH / 2}
              y={prizeTextY}
              textAnchor={showZCoin || isTicketzTournament ? 'start' : 'middle'}
              strokeWidth={6}
            >
              {this.state.prizeString}
            </SVGText>
            <SVGText
              fill='#FFFFFF'
              fontSize={fontSize}
              fontFamily={fontSecondary.fontFamily}
              {...heavyWeight}
              x={showZCoin || isTicketzTournament ? 2 : PRIZE_BACKGROUND_WIDTH / 2}
              y={prizeTextY}
              textAnchor={showZCoin || isTicketzTournament ? 'start' : 'middle'}
            >
              {this.state.prizeString}
            </SVGText>
          </Svg>
        </View>
        <View>
          <Svg height={prizePoolHeight} width={PRIZE_BACKGROUND_WIDTH}>
            <SVGText
              stroke='#2A2A2A'
              strokeOpacity={0.6}
              fontSize={PRIZE_POOL_FONT}
              fontFamily={fontBold.fontFamily}
              {...mediumWeight}
              x={PRIZE_BACKGROUND_WIDTH / 2}
              y={prizePoolHeight - 2}
              textAnchor='middle'
              strokeWidth={2}
            >
              {_m('PRIZE POOL')}
            </SVGText>
            <SVGText
              fill='#FFFFFF'
              fontSize={PRIZE_POOL_FONT}
              fontFamily={fontBold.fontFamily}
              {...mediumWeight}
              x={PRIZE_BACKGROUND_WIDTH / 2}
              y={prizePoolHeight - 2}
              textAnchor='middle'
            >
              {_m('PRIZE POOL')}
            </SVGText>
          </Svg>
        </View>
      </View>
    );
  }

  _renderPrizeAsText(): React.ReactNode {
    const tournament = this.tournament;
    let prizeTextStyle = styles.prizeText;
    const isPoints = tournament.isPointsTournament();
    const threshold = 4; // Threshold overflow value

    if (this.state.prizeString.length >= threshold && isPoints) {
      // Set the text small enough to not overflow
      prizeTextStyle = Sizes.dynamic({
        phone: [styles.prizeText, {fontSize: 24}],
        tablet: [styles.prizeText, {fontSize: 44}],
      });
    }
    if (this.isTournamentTemplateUIV2) {
      return this._renderPrizeAsTextV2();
    }
    return (
      <View>
        <CurrencyString
          amount={this.state.prizeString}
          isGradient={false}
          isTicketzTournament={tournament.isTicketzTournament()}
          isVirtual={isPoints}
          testID={TEST_IDS.MATCH_SELECT_CELL_PRIZE}
          key={tournament.id + '-' + 'currencyPrize' + '-' + this.state.prizeString}
          style={prizeTextStyle}
          ticketzCurrencyIcon={tournament.isTicketzTournament() ? 'ic_ticketz_background.png' : ''}
        />
      </View>
    );
  }

  _renderPrizeAsImage(): React.ReactNode {
    const tournament = this.tournament;
    const extraStyles = this._getBackgroundSizeAdjustment();
    return (
      <View style={[styles.prizeContainer, extraStyles]} testID={TEST_IDS.MATCH_SELECT_CELL_PRIZE}>
        <SkillzImage
          resizeMode={'stretch'}
          source={{uri: tournament.topCustomPrizeImage()}}
          style={this.adjustingStyles.prizeImage}
        />
      </View>
    );
  }

  _renderPrizeContent(): React.ReactNode {
    if (this.tournament.isCustomPrizeTournament() && this.tournament.hasCustomPrizeImage()) {
      return this._renderPrizeAsImage();
    }
    return this._renderPrizeAsText();
  }

  _renderPrizeBackground(): React.ReactNode {
    const tournament = this.tournament;
    const extraStyles = this._getBackgroundSizeAdjustment();
    let categoryType = Tournament.determineTournamentCategory(tournament);
    if (_.get(this.tournaments, 'length') > 1) {
      categoryType = 'SYNC';
    }

    if (!categoryType) {
      if (this.props.displayOnly) {
        // This only happens for sync games for now
        categoryType = 'SYNC';
      }
    }

    return (
      <View style={this.adjustingStyles.prizeContainer}>
        <Optional
          test={
            (!ClientPropertyConstants.getLiveEventHostedEnabled() ||
              !tournament.hasTournamentLogo()) &&
            !tournament.hasLimitedUseTournamentImage()
          }
        >
          <PrizeBackground
            categoryType={categoryType}
            style={[this.adjustingStyles.prizeImage, extraStyles]}
            tournament={tournament}
            fromVSScreen={this.props.fromVSScreen}
          />
        </Optional>
        <Optional
          test={
            ClientPropertyConstants.getLiveEventHostedEnabled() && tournament.hasTournamentLogo()
          }
        >
          <SkillzImage
            key={'hosted_tournament_logo'}
            resizeMode={'stretch'}
            source={this.tournament?.getHostedTournamentLogo()}
            style={[this.adjustingStyles.prizeImage, extraStyles]}
          />
        </Optional>
        <Optional test={tournament.hasLimitedUseTournamentImage()}>
          <SkillzImage
            key={'limited_use_tournament_logo'}
            resizeMode={'stretch'}
            source={this.tournament?.limited_use?.image_url}
            style={[this.adjustingStyles.prizeImage, extraStyles]}
          />
        </Optional>
        <Optional
          test={
            (!ClientPropertyConstants.getLiveEventHostedEnabled() ||
              !tournament.hasTournamentLogo()) &&
            !tournament.hasLimitedUseTournamentImage()
          }
        >
          <View style={styles.prizeCellPadding}>{this._renderPrizeContent()}</View>
        </Optional>
      </View>
    );
  }

  _applyCellRef(view: React.ComponentClass<any, any>) {
    this.cell = view;
  }

  _isHighlightCell(): boolean {
    const tournament = this.tournament;
    // Don't highlight / animate Limited Use Tournaments, regardless of server response
    const shouldHighlightServerTournament =
      tournament.isFocused &&
      FTUEController.hasSeenMatchSelectTutorial() &&
      !tournament.isLimitedUseTournament();
    const shouldHighlightFirstZ =
      tournament.featured &&
      tournament.category_type == 'Z' &&
      !FTUEController.hasSeenMatchSelectTutorial();
    return !this.props.isSyncFTUE && (shouldHighlightFirstZ || shouldHighlightServerTournament);
  }

  // ✅ New method to check for tournament highlight property
  // Use the isHighlighted prop passed down from MatchSelectList
  _shouldBounce(): boolean {
    const tournament = this.tournament;
    const shouldHighlightFirstZ =
      tournament.featured &&
      tournament.category_type == 'Z' &&
      !FTUEController.hasSeenMatchSelectTutorial();
    return !shouldHighlightFirstZ;
  }

  _onLayout(e: unknown) {
    if (this.props.displayOnly) {
      return;
    }

    if (!this.props.isSyncFTUE || (this.props.isSyncFTUE && this._isHighlightCell())) {
      this.props.setPlayButtonCallback(() => {
        this._playButtonTapped();
      });
    }
    const tournament = this.tournament;

    if (this._isHighlightCell()) {
      if (this.scrolled && this.props.setPlayButtonRef instanceof Function) {
        this.props.setPlayButtonRef(this.cell);
        if (Platform.OS === 'ios') {
          const node = findNodeHandle(this.cell);
          if (node) {
            UIManager.measure(
              node,
              (
                x: number,
                y: number,
                width: number,
                height: number,
                rootX: number,
                rootY: number
              ) => {
                const layout = {height, rootX, rootY, width, x, y};
                if (this.props.onHighlight) {
                  this.props.onHighlight(node, layout, tournament);
                }
              }
            );
          }
        } else {
          if (e?.persist) {
            e.persist();
          }
          this.props.onHighlight(findNodeHandle(this.cell), e.nativeEvent, tournament);
        }
      } else {
        this.layoutTimeout = setTimeout(() => {
          this._onLayout(e);
        }, 10); // eslint-disable-line no-magic-numbers
      }
    }
  }

  _generateAccessibilityLabel(): string {
    return this._getTileAccessibilityString();
  }

  _measurePlayButtonForArrow(ref: unknown) {
    if (!FTUEController.hasSeenMatchSelectTutorial() && !this.props.isSyncFTUE) {
      if (ref) {
        const node = findNodeHandle(ref.current);
        if (node) {
          RCTUIManager.measure(
            node,
            (x: number, y: number, width: number, height: number, rootX: number, rootY: number) => {
              const newLayout = {height, rootX, rootY, width, x, y};
              this.props.updatePlayButton?.(newLayout, ref);
            }
          );
        }
      }
    }
  }

  _getCellAdjustment(): Object | undefined {
    if (this.props.useSmallCell) {
      return {height: 35};
    }
  }

  _getBackgroundSizeAdjustment(): Object | undefined {
    if (this.props.useSmallCell) {
      return {width: 55, height: 55};
    }
  }

  _getTextAdjustment(): Object | undefined {
    if (this.props.useSmallCell) {
      return {paddingHorizontal: 1};
    }
  }

  _shouldRenderFriendsBanner(): boolean {
    return (
      this.tournament.isIncomingInvite() &&
      this.props.sectionType === FRIENDS_TOURNAMENT_TYPE &&
      !(
        this.seenTournaments &&
        this.seenTournaments.find((item: Object): boolean => item.id === this.tournament.id)
      )
    );
  }

  _shouldRenderInstaMatch(): boolean {
    return this.props.sectionType === 'INSTAMATCH';
  }

  _shouldRenderInstaMatchDiscounted(tournament: Tournament): boolean {
    return Boolean(this._shouldRenderInstaMatch() && tournament.discounted_cash_fee);
  }

  _shouldRenderEntryFee(): boolean {
    // Check if the tournament is a live bracket
    if (this.tournament.isLiveBracket()) {
      // Do not show the entry fee if player is in any of the following states: Registered, Bracket Full, Waiting for next round
      if (
        !this.tournament.hasTournamentStarted() ||
        this.tournament.isPlayerWaitingForNextRound()
      ) {
        if (this.tournament.isPlayerRegistered() || this.tournament.isBracketFull()) {
          return false;
        }
      }
    }
    // Check if the section type is 'SYNC'
    if (this.props.sectionType === 'SYNC') {
      return false;
    }

    return true;
  }

  _getAvatarURL(): string | undefined {
    if (this.props.avatarUrl) {
      return this.props.avatarUrl;
    } else if (_.get(this.tournament, 'creator.avatar_url')) {
      return _.get(this.tournament, 'creator.avatar_url');
    } else if (this.tournament.isFriendTournament()) {
      return 'ic_placeholder_friends_avatar.png';
    } else {
      return _.get(this.state, 'currentUser.avatar_url');
    }
  }

  _shouldShowAvatar(): boolean {
    return Boolean(
      this.tournament?.friend &&
        (this.tournament?.off_system || this.tournament?.creator?.avatar_url)
    );
  }

  _renderFirstRow(): React.ReactNode {
    const extraStyles = this._getCellAdjustment();
    const avatarUrl = this._getAvatarURL();
    const tournament = this.tournament;
    const imageCornerRadius = 0;
    let avatarStyle = {};

    if (this.tournament.isIncomingInvite()) {
      avatarStyle = this.adjustingStyles.friendsTournamentAvatar;
    } else {
      avatarStyle = this.adjustingStyles.friendsTournamentPlaceholderAvatar;
    }

    const buttonColor =
      ClientPropertyConstants.getLiveEventHostedEnabled() && this.tournament.hasBrandedColor()
        ? [
            this.tournament.getHostedTournamentCustomColor(),
            this.tournament.getHostedTournamentCustomColor(),
          ]
        : playButtonGradient;

    const endCapStyle = this._shouldRenderInstaMatch()
      ? [styles.endCap, styles.endCapInstaMatch]
      : styles.endCap;

    return (
      <View style={[this.adjustingStyles.matchFirstRowContainer, extraStyles]}>
        <Optional test={this._shouldShowAvatar()}>
          <AvatarView
            avatarURL={avatarUrl}
            style={avatarStyle}
            imageCornerRadius={imageCornerRadius}
            backgroundCornerRadius={imageCornerRadius}
          />
        </Optional>
        <View style={styles.matchDescriptionBlock}>{this._renderDescriptionCopy()}</View>
        <Optional test={!this.isTournamentTemplateUIV2}>
          <View collapsable={false} ref={this.playButtonRef} style={endCapStyle}>
            <Optional
              test={
                !this.props.displayOnly && (tournament.hasTournamentStarted() || this.props.isSync)
              }
            >
              <TouchableOpacity
                onPress={() => {
                  this._playGradientButtonTapped();
                }}
                accessibilityLabel={createAccessibilityLabel('PlayButton', {
                  context: this.props.sectionHeader,
                  index: this.props.cellIndex,
                })}
              >
                {this._renderInFlightEventButtonContent(tournament)}
              </TouchableOpacity>
            </Optional>

            <Optional
              test={
                tournament.live_event &&
                !this.props.displayOnly &&
                !tournament.hasTournamentStarted()
              }
            >
              <View style={{opacity: 0}}>
                <PlayGradientButton
                  colors={buttonColor}
                  onPress={() => {}}
                  style={[this.adjustingStyles.playButton, {opacity: 0}]}
                />
              </View>
            </Optional>
          </View>
        </Optional>
      </View>
    );
  }

  _getCellContentStyle(): ViewStyle {
    if (this.props.hasExternalBanner) {
      return styles.roundBottomCorners;
    }
    if (this._shouldRenderInstaMatch()) {
      return {};
    }
    return styles.roundCorners;
  }

  _renderMatchCellInfo(): React.ReactNode {
    return (
      <View style={this.adjustingStyles.matchRowsContainer}>
        {this._renderFirstRow()}
        {this._renderUpcomingEventButton()}
        <Optional test={!this.isTournamentTemplateUIV2}>
          <View style={styles.shadowLineView} />
        </Optional>
        {this._renderMatchDetailsRow()}
      </View>
    );
  }

  _getPlayButtonText(): string {
    if (
      this.tournament.isLiveBracket() &&
      !this.tournament.hasTournamentStarted() &&
      !this.tournament.isPlayerRegistered() &&
      this.tournament.isRegistrationAvailable()
    ) {
      return _m('REGISTER');
    }
    return _m('PLAY');
  }

  _shouldShowNotifyButton(): boolean {
    return LiveBracketTimerUtils.shouldShowNotifyButton(this.tournament);
  }

  _renderDetailsButton() {
    // Use same colors as notify button (primary color from theme)
    const secondaryGradientColors = SkillzStyle.fullStyles.btnGradientTwo;
    const detailsButtonTextGradient = (
      <LinearGradient colors={['#DDDAD6', '#FAF6F1']} style={styles.playButtonV2}>
        <LinearGradient
          colors={['#FFFFFF', '#B0B0B0']}
          style={styles.playButtonInnerShadowGradient}
        >
          <LinearGradient colors={secondaryGradientColors} style={styles.playButtonGradientV2}>
            <Text style={[styles.playButtonText, {fontSize: 13}]} adjustsFontSizeToFit={true}>
              {_m('DETAILS')}
            </Text>
          </LinearGradient>
        </LinearGradient>
      </LinearGradient>
    );

    return (
      <TouchableOpacity
        onPress={() => {
          // Navigate to tournament details
          this._playButtonTapped();
        }}
        style={styles.playButtonV2}
        accessibilityLabel={createAccessibilityLabel('DetailsButton', {
          context: this.props.sectionHeader,
          index: this.props.cellIndex,
        })}
      >
        {detailsButtonTextGradient}
      </TouchableOpacity>
    );
  }

  _renderNotifyButtonV2() {
    const secondaryGradientColors = SkillzStyle.fullStyles.btnGradientTwo;

    const notifyButtonTextGradient = (
      <LinearGradient colors={['#DDDAD6', '#FAF6F1']} style={styles.playButtonV2}>
        <LinearGradient
          colors={['#FFFFFF', '#B0B0B0']}
          style={styles.playButtonInnerShadowGradient}
        >
          <LinearGradient colors={secondaryGradientColors} style={styles.playButtonGradientV2}>
            <Text style={[styles.playButtonText, {fontSize: 13}]} adjustsFontSizeToFit={true}>
              {_m('NOTIFY')}
            </Text>
          </LinearGradient>
        </LinearGradient>
      </LinearGradient>
    );

    return (
      <TouchableOpacity
        onPress={() => {
          // Navigate to tournament details
          this._playButtonTapped();
        }}
        style={styles.playButtonV2}
        accessibilityLabel={createAccessibilityLabel('NotifyButton', {
          context: this.props.sectionHeader,
          index: this.props.cellIndex,
        })}
      >
        {notifyButtonTextGradient}
      </TouchableOpacity>
    );
  }

  _renderPlayButtonV2() {
    const playButtonColors =
      ClientPropertyConstants.getLiveEventHostedEnabled() && this.tournament.hasBrandedColor()
        ? [
            this.tournament.getHostedTournamentCustomColor(),
            this.tournament.getHostedTournamentCustomColor(),
          ]
        : playButtonGradient;

    const playButtonText = this._getPlayButtonText();
    // Show LiveBracketsButton for live brackets when:
    // 1. User is waiting for next round (round is live), OR
    // 2. User is registered/bracket is full AND tournament hasn't started yet
    const shouldShowLiveBracketsButton =
      this.tournament.isLiveBracket() &&
      (this.tournament.isPlayerWaitingForNextRound() ||
        ((this.tournament.isPlayerRegistered() || this.tournament.isBracketFull()) &&
          !this.tournament.hasTournamentStarted()));

    if (shouldShowLiveBracketsButton) {
      return <LiveBracketsButton tournament={this.tournament} />;
    }

    const playButtonTextGradient = (
      <LinearGradient colors={['#DDDAD6', '#FAF6F1']} style={styles.playButtonV2}>
        <LinearGradient
          colors={['#FFFFFF', '#B0B0B0']}
          style={styles.playButtonInnerShadowGradient}
        >
          <LinearGradient colors={playButtonColors} style={styles.playButtonGradientV2}>
            <Text
              style={[styles.playButtonText, {fontSize: playButtonText.length > 4 ? 13 : 15}]}
              adjustsFontSizeToFit={true}
            >
              {this._getPlayButtonText()}
            </Text>
          </LinearGradient>
        </LinearGradient>
      </LinearGradient>
    );

    const mainButton = (
      <TouchableOpacity
        ref={this.playButtonRef}
        onPress={() => {
          this._playGradientButtonTapped();
        }}
        style={this._shouldRenderEntryFee() ? styles.playButtonV2 : styles.playButtonCenteredV2}
        accessibilityLabel={createAccessibilityLabel('PlayButton', {
          context: this.props.sectionHeader,
          index: this.props.cellIndex,
        })}
      >
        {this._shouldShowAnyDiscount() && !this.props.hasAlreadyAnimated ? (
          <SheenEffect showEffect={true} loopDuration={2500}>
            {playButtonTextGradient}
          </SheenEffect>
        ) : (
          playButtonTextGradient
        )}
      </TouchableOpacity>
    );

    // Show notify button if conditions are met
    if (this._shouldShowNotifyButton()) {
      return this._renderNotifyButtonV2();
    }

    // If user has registered for notifications (based on BE field), show appropriate button based on timing
    if (this.tournament.bracket?.to_notify_register_start && this.tournament.isLiveBracket()) {
      // If registration is available, show register button
      if (this.tournament.isRegistrationAvailable()) {
        return mainButton; // This will show the register button
      } else {
        // If registration is not available yet, show details button
        return this._renderDetailsButton();
      }
    }

    return mainButton;
  }

  _renderPlayButtonSection() {
    const tournament = this.tournament;

    // Check if registration has ended and user is not registered
    const isRegistrationClosed =
      tournament.isRegistrationClosed() && !tournament.isPlayerRegistered();

    return (
      <View style={styles.rightContainer}>
        <View style={styles.viewSeperator} />
        <View style={styles.playSectionContainer}>
          {isRegistrationClosed ? (
            <View style={styles.registrationClosedContainer}>
              <Label style={styles.registrationClosedText}>{_m('REGISTRATION')}</Label>
              <Label style={styles.registrationClosedText}>{_m('CLOSED')}</Label>
            </View>
          ) : (
            <>
              <Optional test={this._shouldRenderEntryFee()}>{this._getEntryFeeViewV2()}</Optional>
              <Optional test={!this.props.displayOnly && !this._shouldRenderInstaMatch()}>
                {this._renderPlayButtonV2()}
              </Optional>
            </>
          )}
        </View>
      </View>
    );
  }

  _renderRegistrationBanner(): React.ReactNode {
    const registrationText = LiveBracketTimerUtils.formatRegistrationTime(this.tournament);
    return <RegistrationBanner text={registrationText} />;
  }

  _renderMatchContentContainer(): React.ReactNode {
    let matchContentContainer = (
      <>
        <View
          style={[
            this.adjustingStyles.matchContentContainer,
            this.props.hasCustomBorder && {
              borderWidth: this.props.customBorderProps.borderWidth,
              borderColor: this.props.customBorderProps.borderColor,
            },
            this.props.isHighlighted && !this.props.isHighlightForced
              ? {borderWidth: 5, borderColor: '#FFD41C'}
              : {},
          ]}
        >
          <View style={styles.flowRight}>
            {this._renderPrizeBackground()}
            <Optional test={this._shouldRenderFriendsBanner()}>
              <FriendsNewBanner />
            </Optional>
          </View>
          <Optional test={!this.isTournamentTemplateUIV2}>
            <View style={styles.verticalViewSeperator} />
          </Optional>
          {this._renderMatchCellInfo()}
          <Optional test={this.isTournamentTemplateUIV2}>
            {this._renderPlayButtonSection()}
          </Optional>
        </View>
        {LiveBracketTimerUtils.formatRegistrationTime(this.tournament) !== '' &&
          this._renderRegistrationBanner()}
      </>
    );
    if (this.isTournamentTemplateUIV2) {
      return (
        <LinearGradient colors={['#FFFFFF', '#EDEDED']} style={styles.roundCorners}>
          {matchContentContainer}
        </LinearGradient>
      );
    }
    return matchContentContainer;
  }

  _renderCellContent() {
    const cellContentStyle: ViewStyle[] = [{backgroundColor: 'transparent'}];
    cellContentStyle.push(this._getCellContentStyle());
    const cellPositionStyle: ViewStyle = {position: 'relative', overflow: 'visible'};

    const normalOpacity = 0.9;
    return (
      <TouchableOpacity
        activeOpacity={this.props.displayOnly ? 1 : normalOpacity}
        disabled={this.props.displayOnly}
        onPress={(): void => this._playButtonTapped()}
        onPressIn={(): void => this._playButtonSetHighlighted(true)}
        onPressOut={(): void => this._playButtonSetHighlighted(false)}
        accessibilityLabel={createAccessibilityLabel('MatchSelectCard', {
          context: this.props.sectionHeader,
          index: this.props.cellIndex,
        })}
      >
        <View style={[cellContentStyle, cellPositionStyle]}>
          {this._renderFeaturedText()}
          {this._renderMatchContentContainer()}
          {this._shouldShowPercentageBadge() && (
            <AnimatedDiscountBadge
              style={styles.discountBgImage}
              showAnimation={this._shouldPlayDiscountAnimation()}
              hasAnimated={this.props.hasAlreadyAnimated}
              discountPercentage={this?.tournament?.cash_fee_discount?.discount_percent}
            />
          )}
          <Optional test={!this.props.scaleToContainer}>
            <View style={styles.betweenCellSeperator} />
          </Optional>
        </View>
      </TouchableOpacity>
    );
  }

  _render(): React.ReactNode {
    if (this.tournament.friend && this.state.tournamentExpired) {
      return <View />;
    }
    const testId = this.props.testIDOverride
      ? this.props.testIDOverride
      : this.props.sectionHeader
        ? 'match_select_cell_' + this.props.sectionHeader?.toLowerCase()
        : 'match_select_cell';

    // ✅ Render content with different animations/effects
    let cellContent: React.ReactNode;

    if (this.tournament.isLiveBracket()) {
      cellContent = (
        <Animatable.View
          animation={PULSE_ANIMATION}
          duration={2000}
          iterationCount={'infinite'}
          easing={'linear'}
          useNativeDriver
        >
          <SheenEffect showEffect={true} loopDuration={2500}>
            {this._renderCellContent()}
          </SheenEffect>
        </Animatable.View>
      );
    } else if (this._shouldShowAnyDiscount()) {
      cellContent = (
        <Animatable.View
          animation={
            this._shouldPlayDiscountAnimation() && !this.props.hasAlreadyAnimated
              ? DISCOUNT_PULSE_ANIMATION
              : undefined
          }
          duration={2000}
          delay={3000}
          iterationCount={2}
          easing={Easing.bounce}
          useNativeDriver
        >
          {this._renderCellContent()}
        </Animatable.View>
      );
    } else {
      cellContent = this._renderCellContent();
    }

    // Check if this cell should show non-forced highlight border
    // ✅ Show border ONLY when highlighted AND NOT in forced mode
    const shouldShowInCellHighlight = this.props.isHighlighted && !this.props.isHighlightForced;

    return (
      <View
        accessible={true}
        accessibilityLabel={this._generateAccessibilityLabel()}
        accessibilityRole={'button'}
        testID={testId}
        style={{
          zIndex: shouldShowInCellHighlight ? 999 : undefined, // ✅ Elevate highlighted cell above others
          elevation: shouldShowInCellHighlight ? 999 : 0, // Android elevation
          // ✅ Add space for border at bottom (only in non-forced mode where cell renders its own border)
          // When shouldShowInCellHighlight is false (forced mode or not highlighted), no margin needed
          // marginBottom: shouldShowInCellHighlight ? 10 : 0,
        }}
        onLayout={(event) => {
          // Only measure dimensions when cell is highlighted (to avoid infinite loops)
          if (shouldShowInCellHighlight) {
            const {width, height} = event.nativeEvent.layout;
            if (width > 0 && height > 0) {
              const needsUpdate =
                !this.state.cellDimensions ||
                this.state.cellDimensions.width !== width ||
                this.state.cellDimensions.height !== height;
              if (needsUpdate) {
                this.setState({cellDimensions: {width, height}});
              }
            }
          }
          // Call original onLayout handler
          this._onLayout(event);
        }}
        ref={this._applyCellRef.bind(this)}
        key={testId}
      >
        <Animated.View style={{transform: [{scale: this.bounceValue}]}}>
          {cellContent}
        </Animated.View>

        {/* Non-forced highlight: render border INSIDE cell bounds */}
        {false && shouldShowInCellHighlight && this.state.cellDimensions && (
          <View
            style={{
              position: 'absolute',
              left: 0, // ✅ Keep inside cell bounds
              top: 0, // ✅ Keep inside cell bounds
              width: this.state.cellDimensions.width, // ✅ Match cell width exactly
              height: this.state.cellDimensions.height, // ✅ Match cell height exactly
              zIndex: 1000, // ✅ Higher than cell's z-index to appear above content
              elevation: 1000, // Android
            }}
            pointerEvents='none'
          >
            <TournamentHighlightBorder
              width={this.state.cellDimensions.width}
              height={this.state.cellDimensions.height}
              borderRadius={16}
              startAnimation={false}
              borderColor='#f9eb0d' // 🟡 YELLOW - Production color for non-forced highlights
            />
          </View>
        )}
      </View>
    );
  }

  render(): React.ReactNode {
    // Simplified render - viewport detection now handled by MatchSelectList onViewableItemsChanged
    if (this._isHighlightCell() && !this.scrolled) {
      return <Visibility.Observer target={this}>{this._render()}</Visibility.Observer>;
    }
    return this._render();
  }
}

// 🔥 FIX: Simple shimmer overlay for existing highlight text
const ShimmerOverlay: React.FC = () => {
  const shimmerAnim = useRef(new Animated.Value(-1)).current;
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Start shimmer animation when component mounts
    const startShimmerAnimation = () => {
      Animated.loop(
        Animated.sequence([
          Animated.timing(shimmerAnim, {
            toValue: 1,
            duration: 2000,
            useNativeDriver: true,
          }),
          Animated.delay(1000), // Pause between animations
        ])
      ).start();
    };

    startShimmerAnimation();

    // Hide after 10 seconds to match tournament border behavior
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
    }, 10000);

    return () => clearTimeout(hideTimer);
  }, [shimmerAnim]);

  if (!isVisible) return null;

  const shimmerTranslateX = shimmerAnim.interpolate({
    inputRange: [-1, 1],
    outputRange: [-100, 100],
  });

  return (
    <Animated.View
      style={[
        shimmerOverlayStyles.overlay,
        {
          transform: [{translateX: shimmerTranslateX}],
        },
      ]}
    >
      <LinearGradient
        colors={['transparent', 'rgba(255,255,255,0.6)', 'transparent']}
        style={shimmerOverlayStyles.gradient}
        start={{x: 0, y: 0}}
        end={{x: 1, y: 0}}
      />
    </Animated.View>
  );
};

// 🔥 FIX: Simple shimmer overlay styles
const shimmerOverlayStyles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 50,
    overflow: 'hidden',
  },
  gradient: {
    flex: 1,
    width: '100%',
  },
});
